import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Helmet } from 'react-helmet';
import Header from './Header';
import Footer from './Footer';
import coursesBg from '../images/courses_bg.webp';
import computerImg from '../images/computer.webp';
import frameImg from '../images/Frame_76_1.webp';
import certificateImg from '../images/certificate_temp.png.jpg';
import FormSuccessPopup from './FormSuccessPopup';
import FormLoader from './FormLoader';
import SEO from './SEO';

const CourseDetails = ({ course, onBackClick, onCourseSelect, onHomeClick, onAboutClick, onContactClick, onEnrollClick, activePage, selectedCourse, setIsMobileMenuOpen }) => {
  const heroRef = useRef(null);
  const [isMobileMenuOpen, setLocalMobileMenuOpen] = useState(false);
  const [showSyllabusModal, setShowSyllabusModal] = useState(false);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const carouselRef = useRef(null);
  const [syllabusFormData, setSyllabusFormData] = useState({
    fullName: '',
    mobile: '',
    email: ''
  });
  const [enrollFormData, setEnrollFormData] = useState({
    fullName: '',
    mobile: '',
    email: '',
    location: ''
  });
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState('success');
  const [popupMessage, setPopupMessage] = useState('');
  const [isSyllabusForm, setIsSyllabusForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle mobile menu state
  const handleMobileMenuToggle = (isOpen) => {
    setLocalMobileMenuOpen(isOpen);
    if (setIsMobileMenuOpen) setIsMobileMenuOpen(isOpen);
  };

  // Control call button and Tawk chat visibility based on mobile menu state
  useEffect(() => {
    const callButton = document.getElementById('call-button');
    const tawkChatBox = document.getElementById('tawkchat-minified-box');
    const tawkContainer = document.getElementById('tawkchat-container');

    if (isMobileMenuOpen) {
      if (callButton) callButton.style.display = 'none';
      if (tawkChatBox) tawkChatBox.style.display = 'none';
      if (tawkContainer) tawkContainer.style.display = 'none';
    } else {
      if (callButton) callButton.style.display = 'flex';
      if (tawkChatBox) tawkChatBox.style.display = 'block';
      if (tawkContainer) tawkContainer.style.display = 'block';
    }
  }, [isMobileMenuOpen]);

  useEffect(() => {
    // Scroll to top on mount
    if (heroRef.current) {
      heroRef.current.scrollIntoView({ behavior: 'instant', block: 'start' });
    }
    window.scrollTo(0, 0);
  }, [course]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-in-visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.fade-in-section').forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Prevent scrolling when modals are open
  // Only manage overflow if body is not already locked by mobile menu (position: fixed)
  useEffect(() => {
    const isBodyLocked = document.body.style.position === 'fixed';

    if (showEnrollModal || showSyllabusModal) {
      if (!isBodyLocked) {
        document.body.style.overflow = 'hidden';
      }
    } else {
      if (!isBodyLocked) {
        document.body.style.overflow = 'unset';
      }
    }
    return () => {
      const isBodyLocked = document.body.style.position === 'fixed';
      if (!isBodyLocked) {
        document.body.style.overflow = 'unset';
      }
    };
  }, [showEnrollModal, showSyllabusModal]);

  // Auto-scroll carousel in mobile view
  useEffect(() => {
    const checkAndScroll = () => {
      const isMobile = window.innerWidth <= 768;
      if (!isMobile || !carouselRef.current) return null;

      const interval = setInterval(() => {
        const container = carouselRef.current;
        if (!container) return;

        const card = container.querySelector('div > div');
        if (!card) return;

        const cardWidth = card.offsetWidth;
        const gap = 16;
        const scrollAmount = cardWidth + gap;

        if (container.scrollLeft + container.offsetWidth >= container.scrollWidth - 10) {
          container.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
      }, 2000);

      return interval;
    };

    const interval = checkAndScroll();
    return () => {
      if (interval) clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!course) {
    return (
      <div style={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: '2rem',
        padding: '2rem'
      }}>
        <h2 style={{ color: '#00315d', fontSize: '2rem' }}>No course selected</h2>
        <button
          onClick={onBackClick}
          style={{
            background: 'linear-gradient(135deg, #00315d 0%, #004080 50%, #0066cc 100%)',
            color: 'white',
            padding: '1rem 2rem',
            border: 'none',
            borderRadius: '25px',
            fontSize: '1.1rem',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 4px 15px rgba(0,49,93,0.3)'
          }}
        >
          Back to Home
        </button>
      </div>
    );
  }

  // Course Statistics Data
  // Course Categories for Related Courses
  const courseCategories = {
    'trending-courses': {
      name: 'Trending Courses',
      courses: [
        'Artificial Intelligence & Gen-AI',
        'Prompt Engineering',
        'Cloud Computing',
        'DevOps & SRE',
        'Software Testing',
        'Data Analytics',
        'Data Engineering',
        'Full-Stack Development',
        'Power Platform / Low-Code Automation',
        'Data Science',
        'UI/UX Design'
      ]
    },
    'ai-section': {
      name: 'Artificial Intelligence',
      courses: [
        'Data Science with Gen-AI Training',
        'Generative AI Training',
        'Prompt Engineering Training',
        'Agentic AI Training',
        'Machine Learning Training',
        'Microsoft Copilot Training',
        'AI in Project Management Training',
        'AI in Excel Training',
        'AI Business Analyst Training',
        'AI Chatbot Development Training'
      ]
    },
    'software-testing': {
      name: 'Software Testing',
      courses: [
        'Selenium Automation Training (Java/Python/C#)',
        'Cypress Automation Training (JavaScript)',
        'Playwright Automation Training (TypeScript)',
        'API Testing Training (Postman + RestAssured)',
        'Performance Testing Training (JMeter)',
        'Mobile Automation Training (Appium)'
      ]
    },
    'cloud-computing': {
      name: 'Cloud Computing',
      courses: [
        'AWS Training',
        'Azure Training',
        'GCP Training',
        'Cloud Certification Training'
      ]
    },
    'devops': {
      name: 'DevOps',
      courses: [
        'DevOps Training',
        'Linux Training',
        'Git & GitHub Training',
        'CI/CD Jenkins Training',
        'Docker Training',
        'Kubernetes Training',
        'Terraform Training',
        'Ansible Training',
        'DevSecOps Training',
        'Monitoring & Observability Training'
      ]
    },
    'web-development': {
      name: 'Web Development',
      courses: [
        'React Training',
        'MERN Full-Stack Training',
        'Java Full-Stack Training',
        'Python Full-Stack Training',
        'DOT NET Full-Stack Training'
      ]
    },
    'programming': {
      name: 'Programming',
      courses: [
        'Java Programming',
        'Python Programming',
        'JavaScript Programming',
        'C Programming',
        'C++ Programming',
        'Advanced Python Training',
        'Python with Django Training',
        'Python with Flask Training'
      ]
    },
    'data-engineering': {
      name: 'Data Engineering',
      courses: [
        'Data Engineering Training',
        'Big Data Training',
        'Cloud Data Engineering Training'
      ]
    },
    'testing-addons': {
      name: 'Testing Add-ons',
      courses: [
        'ISTQB Certification Training',
        'SQL for Testers',
        'API Security Testing',
        'Automation Framework Design Training'
      ]
    },
    'power-platform': {
      name: 'Power Platform',
      courses: [
        'Power Apps Training',
        'Power Automate Training',
        'Power BI Training'
      ]
    },
    'mobile-development': {
      name: 'Mobile Development',
      courses: [
        'React Native Training',
        'Flutter Training',
        'Android App Development (Basics)'
      ]
    },
    'ui-ux': {
      name: 'UI/UX Design',
      courses: [
        'UI/UX Design Training',
        'Figma Design Training'
      ]
    }
  };

  const courseStats = {
    'Data Science with Gen-AI Training': { placement: '75–90%', salary: '30–50%', market: '90–95%' },
    'Generative AI Training': { placement: '70–90%', salary: '30–45%', market: '90–95%' },
    'Prompt Engineering Training': { placement: '70–85%', salary: '25–40%', market: '90%' },
    'Agentic AI Training': { placement: '70–85%', salary: '25–40%', market: '90%' },
    'Machine Learning Training': { placement: '65–80%', salary: '25–40%', market: '85%' },
    'AWS Training': { placement: '75–90%', salary: '30–45%', market: '95%' },
    'Azure Training': { placement: '70–90%', salary: '25–40%', market: '90–95%' },
    'GCP Training': { placement: '70–85%', salary: '25–40%', market: '85–90%' },
    'Cloud Certification Training': { placement: '70–85%', salary: '20–35%', market: '80–85%' },
    'DevOps Training': { placement: '80–95%', salary: '30–50%', market: '95%' },
    'Jenkins / CI-CD Training': { placement: '70–85%', salary: '25–40%', market: '90%' },
    'Docker Training': { placement: '70–85%', salary: '20–35%', market: '85–90%' },
    'Kubernetes Training': { placement: '80–95%', salary: '30–50%', market: '95%' },
    'Terraform Training': { placement: '75–90%', salary: '25–40%', market: '90%' },
    'Ansible Training': { placement: '70–85%', salary: '20–30%', market: '80–85%' },
    'DevSecOps Training': { placement: '70–90%', salary: '30–45%', market: '90%' },
    'Monitoring / Observability Training': { placement: '65–80%', salary: '20–35%', market: '75–80%' },
    'Selenium Training': { placement: '70–85%', salary: '20–35%', market: '80–90%' },
    'Playwright Training': { placement: '75–90%', salary: '25–40%', market: '90%' },
    'Cypress Training': { placement: '70–85%', salary: '20–35%', market: '85–90%' },
    'API Testing Training': { placement: '75–90%', salary: '25–40%', market: '90%' },
    'Appium Training': { placement: '65–80%', salary: '20–35%', market: '75–85%' },
    'JMeter Training': { placement: '60–75%', salary: '15–30%', market: '70%' },
    'React Training': { placement: '70–85%', salary: '20–35%', market: '85–90%' },
    'MERN Full Stack Training': { placement: '75–90%', salary: '25–40%', market: '90–95%' },
    'Java Full Stack Training': { placement: '80–95%', salary: '30–45%', market: '95%' },
    'Python Full Stack Training': { placement: '75–90%', salary: '25–40%', market: '90–95%' },
    '.NET Full Stack Training': { placement: '75–90%', salary: '25–40%', market: '90–95%' },
    'Java Training': { placement: '70–85%', salary: '20–35%', market: '85%' },
    'Python Training': { placement: '70–85%', salary: '20–35%', market: '85–90%' },
    'JavaScript Training': { placement: '65–80%', salary: '15–30%', market: '80%' },
    'C / C++ Training': { placement: '60–75%', salary: '10–25%', market: '70%' },
    'Data Engineering Training': { placement: '80–95%', salary: '30–50%', market: '95%' },
    'Big Data Training': { placement: '75–90%', salary: '25–40%', market: '90%' },
    'Cloud Data Engineering Training': { placement: '75–90%', salary: '25–40%', market: '90%' },
    'Power Platform Training': { placement: '70–85%', salary: '20–35%', market: '80–85%' },
    'React Native / Flutter Training': { placement: '65–80%', salary: '20–35%', market: '75–85%' },
    'UI/UX Design Training': { placement: '60–75%', salary: '20–30%', market: '70–80%' }
  };

  const courseModules = {
    // Trending Courses
    'Artificial Intelligence & Gen-AI': [
      'Foundations of AI',
      'Neural Networks & Model Basics',
      'Generative AI Concepts',
      'Prompting Fundamentals',
      'Text, Image & Speech Generation Concepts',
      'Retrieval & Knowledge Integration',
      'Model Optimization Basics',
      'Responsible & Ethical AI',
      'AI Workflow Automation',
      'AI Application Integration',
      'Model Deployment Concepts',
      'Live Project'
    ],
    'Prompt Engineering': [
      'LLM Fundamentals',
      'Prompt Structure & Patterns',
      'System-Level Prompting',
      'Multi-Step Reasoning Techniques',
      'Tool / Function Calling Concepts',
      'Agent Behavior & Task Delegation',
      'Prompt Debugging & Optimization',
      'Business Automation with AI',
      'AI + Workflow Integration',
      'Live Project'
    ],
    'Cloud Computing': [
      'Cloud Fundamentals',
      'Cloud Architecture & Global Infrastructure',
      'Identity & Access Concepts',
      'Compute & Scalability Basics',
      'Storage Technologies',
      'Cloud Networking (Subnets, Routing, Policies)',
      'Load Distribution & High Availability',
      'Serverless Computing',
      'Observability & Monitoring',
      'Infrastructure Automation Principles',
      'Cloud Cost Optimization & Governance',
      'Live Project'
    ],
    'DevOps & SRE': [
      'DevOps Principles & Practices',
      'Version Control Concepts',
      'Continuous Integration Fundamentals',
      'Continuous Delivery & Release Pipelines',
      'Containerization Concepts',
      'Orchestration Principles',
      'Infrastructure Automation',
      'Monitoring & Observability',
      'Cloud-Native Deployments',
      'Reliability Engineering Concepts',
      'Security & Access Automation',
      'Live Project'
    ],
    'Software Testing': [
      'Testing Foundations',
      'Agile Testing & QA Processes',
      'Test Case Design Techniques',
      'Defect Lifecycle Management',
      'Web Automation Concepts',
      'Automation Framework Architecture',
      'Behavior-Driven Testing',
      'API Testing Principles',
      'Mobile Testing Concepts',
      'Cross-Browser & Platform Testing',
      'CI/CD-Integrated Testing',
      'Live Project'
    ],
    'Data Analytics': [
      'Data Analysis Fundamentals',
      'Data Preparation & Cleaning',
      'Data Modeling Concepts',
      'Metrics, KPIs & Business Logic',
      'Dashboard Design Principles',
      'Storytelling with Data',
      'Report Publishing',
      'Automation & Scheduling',
      'Access Controls & Governance',
      'Live Project'
    ],
    'Data Engineering': [
      'Data Engineering Concepts',
      'Distributed Processing Basics',
      'Data Pipelines (Batch & Real-Time)',
      'Storage Systems & Data Lake Concepts',
      'Workflow Orchestration Fundamentals',
      'Data Integration Techniques',
      'Data Quality & Validation',
      'Performance Optimization',
      'Metadata & Governance',
      'Analytical Data Modeling',
      'Live Project'
    ],
    'Full-Stack Development': [
      'Front-End Fundamentals (UI, Layouts, Components)',
      'Client-Side Interactions & State Handling',
      'Backend Architecture Concepts',
      'REST API Design',
      'Database Modeling',
      'Authentication & Authorization',
      'Performance Optimization',
      'Error Handling & Logging',
      'Integration of Frontend–Backend',
      'Deployment Concepts',
      'Application Testing',
      'Live Project'
    ],
    'Power Platform / Low-Code Automation': [
      'Low-Code Development Basics',
      'App Designing Fundamentals',
      'Form & Data Modeling',
      'Workflow Automation Concepts',
      'Integration with Business Data',
      'Process Automation',
      'Dashboarding & Reporting',
      'Access Control & Governance',
      'Enterprise Automation Use Cases',
      'Live Project'
    ],
    'Data Science': [
      'Statistics & Probability Essentials',
      'Data Preparation',
      'Exploratory Data Analysis',
      'Regression & Classification Concepts',
      'Unsupervised Learning',
      'Feature Engineering',
      'Model Evaluation & Metrics',
      'ML Workflow & Pipelines',
      'Intro to Deep Learning Concepts',
      'Model Deployment Basics',
      'Live Project'
    ],
    'UI/UX Design': [
      'Design Thinking',
      'User Research & Persona Creation',
      'Information Architecture',
      'Wireframes & Prototypes',
      'Visual Design Fundamentals',
      'Interaction Design',
      'Design Systems',
      'Usability Testing',
      'Product Handoff',
      'Live Project'
    ],

    // AI Courses
    'Data Science with Gen-AI Training': [
      'Python for Data Analysis',
      'Statistics & Probability',
      'Data Cleaning & Preparation',
      'Exploratory Data Analysis',
      'Machine Learning Concepts',
      'Regression & Classification',
      'Unsupervised Learning',
      'Gen-AI Fundamentals',
      'Prompting + LLM Integration',
      'Model Evaluation & Metrics',
      'ML Pipelines & Automation',
      'Model Deployment Concepts',
      'Industry Use-Case Workshop'
    ],
    'Generative AI Training': [
      'Intro to Generative AI',
      'Large Language Models Basics',
      'Text, Image & Speech Generation Concepts',
      'Prompting Fundamentals',
      'RAG Concepts (Retrieval + AI)',
      'Vector Storage Fundamentals',
      'Workflow Automation with Gen-AI',
      'Agents & Task Automation',
      'Evaluation & Optimization',
      'AI Integrations with Applications',
      'Industry Use-Case Workshop'
    ],
    'Prompt Engineering Training': [
      'LLLM Basics & Token Behavior',
      'Prompt Structures & Patterns',
      'System, Developer & User Prompts',
      'Chain-of-Thought & Multi-Step Prompts',
      'Tool & Function Calling Concepts',
      'Multi-Agent Prompting',
      'Prompt Debugging & Refinement',
      'AI Workflow Automation',
      'Business Use Cases',
      'Industry Use-Case Workshop'
    ],
    'Agentic AI Training': [
      'Agent Architecture Fundamentals',
      'Task Planning & Reasoning',
      'Memory & Context Handling',
      'Tool Invocation Concepts',
      'Multi-Agent Collaboration',
      'Workflow Engines for Agents',
      'Knowledge Integration',
      'Monitoring & Control',
      'Real-World Automation Flows',
      'Industry Use-Case Workshop'
    ],
    'Machine Learning Training': [
      'Python Essentials',
      'Statistics for ML',
      'Data Preprocessing',
      'ML Model Building',
      'Supervised Learning',
      'Unsupervised Learning',
      'Feature Engineering',
      'Model Evaluation',
      'Optimization Techniques',
      'Production ML Basics',
      'Industry Use-Case Workshop'
    ],
    'Microsoft Copilot Training': [
      'Introduction to Copilot',
      'Copilot for Office Apps',
      'Copilot for Email, Summaries & Drafting',
      'Workflow Automation with Copilot',
      'Data Insights with Copilot',
      'Security & Governance Basics',
      'Real Use Cases',
      'Industry Use-Case Workshop'
    ],
    'AI in Project Management Training': [
      'AI Overview for Project Managers',
      'AI for Requirement Analysis',
      'Sprint Planning with AI Tools',
      'Task Estimation Automation',
      'AI-driven Risk & Issue Tracking',
      'AI-Enabled Reporting',
      'Status & Dashboard Automation',
      'Industry Use-Case Workshop'
    ],
    'AI in Excel Training': [
      'Excel + AI Basics',
      'Formula Generation with AI',
      'Dataset Cleanup with AI',
      'Pivot + Dashboard Automation',
      'Insights & Predictive Analysis',
      'Report Generation',
      'Real Use Cases',
      'Industry Use-Case Workshop'
    ],
    'AI Business Analyst Training': [
      'BA Foundations',
      'Requirement Analysis using AI',
      'Process Mapping with AI Tools',
      'AI-Driven Documentation',
      'Data Interpretation with AI',
      'Automated Reporting',
      'Creating BRDs, SRS using AI',
      'Stakeholder Communication',
      'Industry Use-Case Workshop'
    ],
    'AI Chatbot Development Training': [
      'Chatbot Architecture',
      'Conversation Design',
      'Intent & Context Handling',
      'Prompting for Conversations',
      'Memory & History',
      'Workflow Integration',
      'Knowledge Base Integration',
      'Evaluation & Optimization',
      'Deployment Concepts',
      'Industry Use-Case Workshop'
    ],

    // Software Testing Courses
    'Selenium Automation Training (Java/Python/C#)': [
      'Core Java Essentials',
      'Selenium WebDriver Concepts',
      'Locators & Web Element Operations',
      'Waits, Frames, Windows, Actions',
      'TestNG Framework',
      'Page Object Model (POM)',
      'Hybrid Framework Architecture',
      'Data-Driven Testing',
      'BDD with Cucumber',
      'Automation Best Practices',
      'CI/CD Integration Basics',
      'Industry Use-Case Workshop'
    ],
    'Cypress Automation Training (JavaScript)': [
      'JavaScript Basics for Testing',
      'Cypress Setup & Test Runner',
      'Locators, Assertions & Commands',
      'Page Object Model in Cypress',
      'API Testing with Cypress',
      'Fixtures & Custom Commands',
      'Retry-Ability & Flaky Test Handling',
      'Parallel Testing Concepts',
      'Reporting & Screenshots',
      'CI/CD Integration',
      'Industry Use-Case Workshop'
    ],
    'Playwright Automation Training (TypeScript)': [
      'TypeScript Basics',
      'Playwright Installation & Test Runner',
      'Locators, Assertions & Actions',
      'Page Object Model with Playwright',
      'Parallel & Cross-Browser Execution',
      'API Testing Concepts',
      'Authentication Handling',
      'Fixtures & Test Hooks',
      'Reporting & Tracing',
      'CI/CD Pipeline Execution',
      'Industry Use-Case Workshop'
    ],
    'API Testing Training (Postman + RestAssured)': [
      'API Fundamentals',
      'REST Architecture Concepts',
      'API Functional Testing using Postman',
      'Collections, Environments & Assertions',
      'API Automation Using Java',
      'RestAssured Framework Basics',
      'Request/Response Validation',
      'Data-Driven API Testing',
      'Reporting & Logging',
      'Integration with CI/CD',
      'Industry Use-Case Workshop'
    ],
    'Performance Testing Training (JMeter)': [
      'Performance Testing Concepts',
      'JMeter Components & Test Plan',
      'Thread Groups & Samplers',
      'Timers, Assertions & Controllers',
      'Parameterization & Correlation',
      'Distributed Load Testing',
      'Monitoring & Performance Metrics',
      'Result Analysis & Reporting',
      'CI/CD Integration',
      'Industry Use-Case Workshop'
    ],
    'Mobile Automation Training (Appium)': [
      'Mobile Testing Concepts',
      'Appium Setup & Drivers',
      'Locators & Gestures',
      'Native / Hybrid / Web App Testing',
      'Page Object Model for Mobile',
      'App Automation Workflows',
      'Handling Permissions, Alerts & Popups',
      'Parallel Execution & Grid',
      'Reporting & Logs',
      'CI/CD Execution for Mobile Tests',
      'Industry Use-Case Workshop'
    ],

    // Cloud Computing Courses
    'AWS Training': [
      'Cloud Fundamentals & Architecture',
      'Identity & Access Concepts',
      'Compute Services & Scalability Practices',
      'Storage Models & Data Lifecycle',
      'Virtual Networking, Routing & Security',
      'Load Balancing & High Availability',
      'Serverless Computing Concepts',
      'Monitoring, Logging & Alerts',
      'Infrastructure Automation (Templates + IaC Basics)',
      'Cloud Security Essentials',
      'Cost Optimization Techniques',
      'Industry Use-Case Workshop'
    ],
    'Azure Training': [
      'Azure Global Architecture',
      'Directory & Identity Fundamentals',
      'Compute & Virtualization Concepts',
      'Storage Accounts & Data Types',
      'Virtual Networks, Routing & Traffic Rules',
      'Load Distribution & Redundancy',
      'Serverless Functions & Event Workflows',
      'Monitoring, Metrics, Logs & Insights',
      'Template-Based Automation Concepts',
      'Security, Compliance & Policies',
      'Cost Management & Governance',
      'Industry Use-Case Workshop'
    ],
    'GCP Training': [
      'GCP Architecture & Resource Model',
      'IAM & Role-Based Access',
      'Virtual Machine Concepts',
      'Cloud Storage & Data Handling',
      'Virtual Private Cloud Networking',
      'Load Management & Distribution',
      'Event-Driven & Serverless Concepts',
      'Logs, Metrics & Observability',
      'Infrastructure Automation Basics',
      'Cost Controls & Budget Policies',
      'Security Principles & Compliance',
      'Industry Use-Case Workshop'
    ],
    'Cloud Certification Training': [
      'Cloud Concepts & Architecture Breakdown',
      'Identity, Access & Security Domains',
      'Compute, Storage & Networking Domain Coverage',
      'High Availability & Fault Tolerance Concepts',
      'Monitoring & Operational Excellence',
      'Cloud Deployment & Automation Concepts',
      'Scenario-Based Architecture Questions',
      'Practice Labs & Hands-On Exercises',
      'Exam Blueprint Walkthrough',
      'Mock Exams & Question-Solving Strategies',
      'Certification Readiness Review',
      'Industry Use-Case Workshop'
    ],

    // DevOps Courses
    'DevOps Training': [
      'DevOps Principles & Practices',
      'Linux Essentials',
      'Git & Code Collaboration',
      'CI/CD Pipeline Fundamentals',
      'Containerization Concepts',
      'Orchestration Basics',
      'Infrastructure Automation Foundations',
      'Cloud Deployment Concepts',
      'Configuration Management Basics',
      'DevSecOps Principles',
      'Monitoring & Observability Concepts',
      'Industry Use-Case Workshop'
    ],
    'Linux Training': [
      'Linux Architecture',
      'Command Line & File Operations',
      'User & Group Management',
      'Package Management',
      'Process & Service Control',
      'Shell Scripting Basics',
      'Networking Commands',
      'Permissions & Security',
      'Industry Use-Case Workshop'
    ],
    'Git & GitHub Training': [
      'Git Basics & Architecture',
      'Branching & Tagging',
      'Merge & Conflict Handling',
      'GitHub Workflow',
      'Pull Requests & Code Reviews',
      'GitHub Actions Basics',
      'Industry Use-Case Workshop'
    ],
    'CI/CD Jenkins Training': [
      'CI/CD Fundamentals',
      'Jenkins Architecture',
      'Freestyle Jobs',
      'Pipeline as Code',
      'Build Automation',
      'Integration with SCM',
      'Testing & Reporting',
      'Deployment Automation',
      'User & Credential Management',
      'Industry Use-Case Workshop'
    ],
    'Docker Training': [
      'Container Concepts',
      'Docker Architecture',
      'Images, Containers & Layers',
      'Volumes & Persistent Storage',
      'Docker Networking',
      'Multi-Container Apps',
      'Docker Compose',
      'Container Security Basics',
      'Industry Use-Case Workshop'
    ],
    'Kubernetes Training': [
      'Kubernetes Architecture',
      'Pods, ReplicaSets & Deployments',
      'Services & Networking',
      'ConfigMaps & Secrets',
      'Persistent Storage',
      'Ingress Concepts',
      'Autoscaling',
      'Rolling Updates & Rollbacks',
      'Helm Charts Basics',
      'Industry Use-Case Workshop'
    ],
    'Terraform Training': [
      'IaC Fundamentals',
      'Providers & Resources',
      'Modules & Reusability',
      'Variables, Outputs & State Files',
      'Workspaces',
      'Policy & Governance Basics',
      'Terraform & CI/CD',
      'Industry Use-Case Workshop'
    ],
    'Ansible Training': [
      'Configuration Management Basics',
      'YAML & Inventory Concepts',
      'Playbooks & Task Automation',
      'Roles & Reusable Components',
      'Variables, Templates & Handlers',
      'Idempotency Concepts',
      'Ansible for Application Deployment',
      'Ansible + CI/CD Integration',
      'Industry Use-Case Workshop'
    ],
    'DevSecOps Training': [
      'DevSecOps Principles',
      'Shift-Left Security',
      'Secure Coding Concepts',
      'Security Scanning in CI/CD',
      'Dependency & Vulnerability Analysis',
      'Secrets Management',
      'Container Security',
      'Security Policies & Compliance',
      'Industry Use-Case Workshop'
    ],
    'Monitoring & Observability Training': [
      'Monitoring vs Observability',
      'Metrics, Logs & Traces',
      'Dashboard Creation Principles',
      'Alerting Strategies',
      'Log Aggregation Basics',
      'Application & Infrastructure Monitoring',
      'SLI / SLO / SLA Concepts',
      'Performance Optimization Insights',
      'Industry Use-Case Workshop'
    ],

    // Web Development Courses
    'React Training': [
      'HTML, CSS, JS Essentials',
      'React Basics & JSX',
      'Components & Props',
      'State & Events',
      'Hooks (useState, useEffect, useRef, useMemo)',
      'Routing',
      'Form Handling & Validation',
      'API Integration',
      'State Management Basics',
      'Performance Optimization',
      'Reusable Components',
      'Industry Use-Case Workshop'
    ],
    'MERN Full-Stack Training': [
      'Front-End Essentials (HTML, CSS, JS)',
      'React Components & Hooks',
      'Routing & State',
      'Node.js Backend Basics',
      'Express Routing',
      'MongoDB Modeling',
      'Authentication & Authorization',
      'API Integration',
      'Error Handling',
      'Deployment Concepts',
      'Testing Basics',
      'Industry Use-Case Workshop'
    ],
    'Java Full-Stack Training': [
      'Core Java (OOP, Collections)',
      'Java Web Development Basics',
      'REST API Concepts',
      'Backend Architecture (MVC Layering)',
      'SQL & Database Integration',
      'HTML, CSS, JavaScript Frontend',
      'Client–Server Integration',
      'Authentication & Session Handling',
      'Deployment Principles',
      'Debugging & Testing',
      'Industry Use-Case Workshop'
    ],
    'Python Full-Stack Training': [
      'Python Programming Basics',
      'Backend Architecture Concepts',
      'REST API Development Flow',
      'Database Integration (SQL)',
      'HTML, CSS, JS for Frontend',
      'Template Rendering Concepts',
      'Authentication & Access Handling',
      'API Integration with Frontend',
      'Deployment Concepts',
      'Debugging & Testing',
      'Industry Use-Case Workshop'
    ],
    'DOT NET Full-Stack Training': [
      'C# Programming Fundamentals',
      '.NET Web App Concepts',
      'API Development Flow',
      'Backend Architecture Patterns',
      'SQL Server Integration',
      'HTML, CSS, JS Frontend',
      'Client–Server Communication',
      'Authentication & Authorization',
      'Deployment Workflow',
      'Application Testing',
      'Industry Use-Case Workshop'
    ],

    // Programming Courses
    'Java Programming': [
      'Java Basics',
      'OOP Concepts',
      'Collections',
      'Exception Handling',
      'File Handling',
      'Multithreading',
      'Streams & Lambdas',
      'JDBC Basics',
      'Industry Use-Case Workshop'
    ],
    'Python Programming': [
      'Python Basics',
      'Data Types & Operators',
      'Control Flow',
      'Functions & Modules',
      'File Handling',
      'OOP in Python',
      'Virtual Environments & Pip',
      'Automation Basics',
      'Industry Use-Case Workshop'
    ],
    'JavaScript Programming': [
      'JS Basics',
      'DOM Manipulation',
      'Events',
      'ES6 Concepts',
      'Async Programming',
      'Error Handling',
      'Modules & Best Practices',
      'Industry Use-Case Workshop'
    ],
    'C Programming': [
      'C Basics',
      'Data Types & Operators',
      'Loops & Conditions',
      'Arrays & Strings',
      'Functions & Pointers',
      'Structures & Unions',
      'File Handling',
      'Memory Management Basics',
      'Industry Use-Case Workshop'
    ],
    'C++ Programming': [
      'C++ Basics',
      'OOP Concepts',
      'Constructors & Destructors',
      'Inheritance & Polymorphism',
      'Templates',
      'Exception Handling',
      'STL Basics',
      'File I/O',
      'Industry Use-Case Workshop'
    ],
    'Advanced Python Training': [
      'Advanced Functions',
      'Decorators & Generators',
      'File & OS Automation',
      'Logging & Error Handling',
      'OOP Deep-Dive',
      'Working with JSON, CSV, XML',
      'Multithreading & Multiprocessing',
      'Industry Use-Case Workshop'
    ],
    'Python with Django Training': [
      'Django Fundamentals',
      'MVT Architecture',
      'Models & ORM',
      'Templates & Views',
      'URL Routing',
      'Forms & Validation',
      'Authentication',
      'API Development Basics',
      'Deployment Concepts',
      'Industry Use-Case Workshop'
    ],
    'Python with Flask Training': [
      'Flask Basics',
      'Routing & Views',
      'Templates',
      'Request Handling',
      'Database Integration',
      'REST API Design',
      'Authentication Basics',
      'Deployment Flow',
      'Industry Use-Case Workshop'
    ],

    // Data Engineering Courses
    'Data Engineering Training': [
      'Data Engineering Basics',
      'Data Modeling Concepts',
      'ETL & ELT Workflows',
      'Batch & Stream Processing',
      'Distributed Data Concepts',
      'Workflow Orchestration',
      'Data Quality & Validation',
      'Data Lake Concepts',
      'Lakehouse Architecture',
      'Governance & Metadata',
      'Industry Use-Case Workshop'
    ],
    'Big Data Training': [
      'Big Data Concepts',
      'Distributed Architecture',
      'Parallel Processing Concepts',
      'Storage & File Formats',
      'Data Pipeline Design',
      'Cluster Concepts',
      'Fault Tolerance & Recovery',
      'Performance Optimization',
      'Industry Use-Case Workshop'
    ],
    'Cloud Data Engineering Training': [
      'Cloud Data Fundamentals',
      'Storage & Warehouse Concepts',
      'Scalable Data Pipelines',
      'Orchestration Concepts',
      'Real-Time Data Flow',
      'Analytical Modeling',
      'Automation & Governance',
      'Industry Use-Case Workshop'
    ],

    // Testing Add-ons
    'ISTQB Certification Training': [
      'Fundamentals of Testing',
      'SDLC & STLC',
      'Test Design Techniques',
      'Test Management',
      'Tool Support for Testing',
      'Exam Question Patterns',
      'Industry Use-Case Workshop'
    ],
    'SQL for Testers': [
      'Basics of RDBMS',
      'Select, Insert, Update, Delete',
      'Joins, Grouping & Filtering',
      'Subqueries',
      'Data Validation Techniques',
      'Industry Use-Case Workshop'
    ],
    'API Security Testing': [
      'API Security Basics',
      'Authentication & Tokens',
      'Common Vulnerabilities',
      'Input Validation',
      'Broken Access Control',
      'Industry Use-Case Workshop'
    ],
    'Automation Framework Design Training': [
      'Framework Lifecycle',
      'POM Structure',
      'Utilities & Helper Layers',
      'Reporting Integration',
      'Reusable Keyword Layers',
      'CI/CD Execution Model',
      'Industry Use-Case Workshop'
    ],

    // Power Platform Courses
    'Power Apps Training': [
      'App Design Basics',
      'Canvas & Form Design',
      'Data Modeling',
      'Business Logic',
      'Integration Concepts',
      'Security & Sharing',
      'Industry Use-Case Workshop'
    ],
    'Power Automate Training': [
      'Flow Types',
      'Triggers & Actions',
      'Automation Logic',
      'Connectors',
      'Error Handling',
      'Approvals & Notifications',
      'Industry Use-Case Workshop'
    ],
    'Power BI Training': [
      'Data Loading & Cleaning',
      'Data Modeling',
      'DAX Basics',
      'Visualizations',
      'KPIs & Dashboards',
      'Publishing & Sharing',
      'Security Rules',
      'Industry Use-Case Workshop'
    ],

    // Mobile Development Courses
    'React Native Training': [
      'React Native Basics',
      'Components & Props',
      'Navigation',
      'Forms & APIs',
      'Storage Concepts',
      'State Management Basics',
      'Deployment Concepts',
      'Industry Use-Case Workshop'
    ],
    'Flutter Training': [
      'Dart Basics',
      'Widgets & UI Layouts',
      'Routing & State',
      'API Integration',
      'Local Storage',
      'Animations',
      'Publishing Concepts',
      'Industry Use-Case Workshop'
    ],
    'Android App Development (Basics)': [
      'Android Basics',
      'UI Components',
      'Layouts & Navigation',
      'API Calls',
      'Data Storage',
      'Notifications',
      'Industry Use-Case Workshop'
    ],

    // UI/UX Design Courses
    'UI/UX Design Training': [
      'Design Thinking',
      'User Research',
      'Wireframes & User Flows',
      'Prototyping',
      'Visual Design Principles',
      'Interaction Design',
      'Usability Testing',
      'Industry Use-Case Workshop'
    ],
    'Figma Design Training': [
      'Figma Basics',
      'Frames & Layouts',
      'Components & Variants',
      'Prototyping',
      'Design Systems',
      'Collaborative Design',
      'Industry Use-Case Workshop'
    ]
  };

  const modules = courseModules[course] || [
    'Course Fundamentals',
    'Core Concepts',
    'Advanced Topics',
    'Best Practices',
    'Tools and Technologies',
    'Industry Standards',
    'Real-World Projects',
    'Certification Preparation'
  ];

  // Course-specific durations based on complexity and industry standards
  const courseDurations = {
    // Trending Courses (AI Courses - Longer duration due to complexity)
    'Artificial Intelligence & Gen-AI': '10–12 Weeks',
    'Prompt Engineering': '4–6 Weeks',
    'Cloud Computing': '10–12 Weeks',
    'DevOps & SRE': '12 Weeks',
    'Software Testing': '10–12 Weeks',
    'Data Analytics': '6–8 Weeks',
    'Data Engineering': '10–12 Weeks',
    'Full-Stack Development': '16 Weeks',
    'Power Platform / Low-Code Automation': '6–8 Weeks',
    'Data Science': '12 Weeks',
    'UI/UX Design': '8–10 Weeks',

    // AI Section
    'Data Science with Gen-AI Training': '16–20 Weeks',
    'Generative AI Training': '10–12 Weeks',
    'Prompt Engineering Training': '6–8 Weeks',
    'Agentic AI Training': '8–10 Weeks',
    'Machine Learning Training': '12–14 Weeks',
    'Microsoft Copilot Training': '4–6 Weeks',
    'AI in Project Management Training': '4–6 Weeks',
    'AI in Excel Training': '3–4 Weeks',
    'AI Business Analyst Training': '6–8 Weeks',
    'AI Chatbot Development Training': '8–10 Weeks',

    // Testing Courses
    'Selenium Automation Training (Java/Python/C#)': '8–10 Weeks',
    'Cypress Automation Training (JavaScript)': '6–8 Weeks',
    'Playwright Automation Training (TypeScript)': '6–8 Weeks',
    'API Testing Training (Postman + RestAssured)': '6–8 Weeks',
    'Performance Testing Training (JMeter)': '4–6 Weeks',
    'Mobile Automation Training (Appium)': '8–10 Weeks',

    // Cloud Courses
    'AWS Training': '12–14 Weeks',
    'Azure Training': '12–14 Weeks',
    'GCP Training': '10–12 Weeks',
    'Cloud Certification Training': '8–10 Weeks',

    // DevOps Courses
    'DevOps Training': '14–16 Weeks',
    'Linux Training': '4–6 Weeks',
    'Git & GitHub Training': '3–4 Weeks',
    'CI/CD Jenkins Training': '6–8 Weeks',
    'Docker Training': '4–6 Weeks',
    'Kubernetes Training': '8–10 Weeks',
    'Terraform Training': '6–8 Weeks',
    'Ansible Training': '4–6 Weeks',
    'DevSecOps Training': '6–8 Weeks',
    'Monitoring & Observability Training': '4–6 Weeks',

    // Full-Stack Development
    'React Training': '8–10 Weeks',
    'MERN Full-Stack Training': '16–20 Weeks',
    'Java Full-Stack Training': '20–24 Weeks',
    'Python Full-Stack Training': '18–22 Weeks',
    'DOT NET Full-Stack Training': '20–24 Weeks',

    // Programming
    'Java Programming': '8–10 Weeks',
    'Python Programming': '8–10 Weeks',
    'JavaScript Programming': '6–8 Weeks',
    'C Programming': '6–8 Weeks',
    'C++ Programming': '8–10 Weeks',
    'Advanced Python Training': '6–8 Weeks',
    'Python with Django Training': '10–12 Weeks',
    'Python with Flask Training': '8–10 Weeks',

    // Data Engineering
    'Data Engineering Training': '16–20 Weeks',
    'Big Data Training': '12–14 Weeks',
    'Cloud Data Engineering Training': '10–12 Weeks',

    // Testing Add-ons
    'ISTQB Certification Training': '4–6 Weeks',
    'SQL for Testers': '3–4 Weeks',
    'API Security Testing': '4 Weeks',
    'Automation Framework Design Training': '4–6 Weeks',

    // Power Platform
    'Power Apps Training': '6–8 Weeks',
    'Power Automate Training': '4–6 Weeks',
    'Power BI Training': '6–8 Weeks',

    // Mobile Development
    'React Native Training': '10–12 Weeks',
    'Flutter Training': '10–12 Weeks',
    'Android App Development (Basics)': '8–10 Weeks',

    // UI/UX Design
    'UI/UX Design Training': '8–10 Weeks',
    'Figma Design Training': '4–6 Weeks'
  };

  const courseDuration = courseDurations[course] || '8–10 Weeks';

  // Function to get related courses from the same category
  const getRelatedCourses = () => {
    for (const categoryData of Object.values(courseCategories)) {
      if (categoryData.courses.includes(course)) {
        // Return other courses from the same category, excluding the current course
        return categoryData.courses.filter(c => c !== course).slice(0, 4);
      }
    }
    // If not found in any category, return trending courses
    return courseCategories['trending-courses'].courses.slice(0, 4);
  };

  const relatedCourses = getRelatedCourses();

  // Course overviews from production
  const courseOverviews = {
    'Artificial Intelligence & Gen-AI': 'Learn core AI concepts, model fundamentals, generative AI workflows, and real-time AI implementation.',
    'Prompt Engineering': 'Master the science of effective prompting, structured instructions, multi-step reasoning, and business automation using LLMs.',
    'Cloud Computing': 'Learn all cross-platform cloud concepts including compute, networking, storage, automation, governance, and cloud architecture.',
    'DevOps & SRE': 'Learn CI/CD pipelines, automation, containerization, orchestration, monitoring, and deployment workflows.',
    'Software Testing': 'Full coverage of manual testing, automation concepts, API validation, mobile testing, and end-to-end QA processes.',
    'Data Analytics': 'Learn data cleaning, modeling, visualisation, KPIs, reporting and interpretation for business decision-making.',
    'Data Engineering': 'Covers pipelines, batch/stream processing, orchestration, storage design, and enterprise data architecture.',
    'Full-Stack Development': 'Build modern web applications using generic front-end, back-end, and database concepts.',
    'Power Platform / Low-Code Automation': 'Build business apps, workflows, dashboards using low-code techniques.',
    'Data Science': 'Master statistics, machine learning concepts, model evaluation and deployment fundamentals.',
    'UI/UX Design': 'Learn product design principles, user research, wireframing, prototyping, and design systems.',

    // AI Courses
    'Data Science with Gen-AI Training': 'Complete data science + Gen-AI workflow training with analytics, ML, and LLM integration.',
    'Generative AI Training': 'Learn LLMs, AI generation workflows, automation, and building Gen-AI-driven applications.',
    'Prompt Engineering Training': 'Master advanced prompting methods, reasoning patterns, and AI workflow optimization.',
    'Agentic AI Training': 'Learn to build AI agents that perform tasks autonomously, connect tools, and automate workflows.',
    'Machine Learning Training': 'End-to-end ML pipeline training covering algorithms, workflows, and deployment basics.',
    'Microsoft Copilot Training': 'Learn AI-powered automation across productivity apps for business, documentation, and enterprise workflows.',
    'AI in Project Management Training': 'Use AI tools and automation to handle planning, estimation, documentation, and monitoring.',
    'AI in Excel Training': 'AI-powered Excel automation for formulas, reporting, dashboards, and business tasks.',
    'AI Business Analyst Training': 'AI-driven requirement gathering, process modelling, document automation, and business insights.',
    'AI Chatbot Development Training': 'Design and build AI chatbots using LLM concepts and workflow integrations.',

    // Testing Courses
    'Selenium Automation Training (Java/Python/C#)': 'Web automation using Selenium with modern framework design practices.',
    'Cypress Automation Training (JavaScript)': 'Modern web automation with Cypress using JavaScript and component-level testing.',
    'Playwright Automation Training (TypeScript)': 'Fast, reliable, cross-browser automation using Playwright with TypeScript.',
    'API Testing Training (Postman + RestAssured)': 'End-to-end API testing using Postman for functional validation and RestAssured for automation.',
    'Performance Testing Training (JMeter)': 'Load, stress, endurance, and performance test execution using JMeter.',
    'Mobile Automation Training (Appium)': 'Mobile app automation for Android & iOS using Appium with modern frameworks.',

    // Cloud Courses
    'AWS Training': 'Complete cloud computing training covering compute, storage, networking, identity, automation, and modern cloud architecture on AWS.',
    'Azure Training': 'Learn core Azure platform services, cloud identity, networking, compute, monitoring, and cloud governance.',
    'GCP Training': 'Training focused on Google Cloud core services, compute, networking, automation, IAM, monitoring, and cloud-native architecture.',
    'Cloud Certification Training': 'Certification-focused training covering exam domains, cloud fundamentals, scenario-based questions, architecture case studies, hands-on labs, and exam preparation strategies for major cloud certifications.',

    // DevOps Courses
    'DevOps Training': 'Complete DevOps lifecycle training covering collaboration, CI/CD, automation, cloud, containerization, monitoring, and security integrations.',
    'Linux Training': 'Core Linux skills for DevOps, cloud, and automation environments.',
    'Git & GitHub Training': 'Version control and collaboration workflows essential for DevOps pipelines.',
    'CI/CD Jenkins Training': 'Build, test, deploy workflows using Jenkins and automation pipelines.',
    'Docker Training': 'Containerization foundations including images, containers, networking, and multi-service setups.',
    'Kubernetes Training': 'Production-grade container orchestration for managing workloads at scale.',
    'Terraform Training': 'Infrastructure-as-Code automation for provisioning cloud resources.',
    'Ansible Training': 'Agentless configuration management and automation for IT infrastructure.',
    'DevSecOps Training': 'Security integrated into development, automation, CI/CD, and cloud workflows.',
    'Monitoring & Observability Training': 'Complete monitoring stack covering logs, metrics, traces, alerting, and dashboards.',

    // Web Development
    'React Training': 'Modern front-end development using React, components, hooks, routing, and state management.',
    'MERN Full-Stack Training': 'Complete end-to-end development using MongoDB, Express, React, and Node.js.',
    'Java Full-Stack Training': 'Enterprise-level full-stack development using Java, backend architecture, and front-end integration.',
    'Python Full-Stack Training': 'Full-stack development using Python backend concepts and modern front-end tools.',
    'DOT NET Full-Stack Training': 'Complete .NET-based application development with structured backend design and UI integration.',

    // Programming
    'Java Programming': 'Core-to-advanced Java concepts for backend and automation roles.',
    'Python Programming': 'Complete Python fundamentals for automation, backend, and data workflows.',
    'JavaScript Programming': 'JavaScript for web development and automation scripting.',
    'C Programming': 'Structured programming with C for system-level understanding and logical foundations.',
    'C++ Programming': 'Object-oriented development with C++ for system programming and backend logic.',
    'Advanced Python Training': 'Advanced Python for automation, data processing, and enterprise-level coding.',
    'Python with Django Training': 'Backend web framework with Django for dynamic application development.',
    'Python with Flask Training': 'Lightweight backend development with Flask for APIs and microservices.',

    // Data Engineering
    'Data Engineering Training': 'End-to-end enterprise data pipeline, modeling, orchestration, and processing concepts.',
    'Big Data Training': 'Foundational skills in distributed datasets, processing, and scalable storage ecosystems.',
    'Cloud Data Engineering Training': 'Data engineering workflows using cloud storage, compute, orchestration, and analytics.',

    // Testing Add-ons
    'ISTQB Certification Training': 'Syllabus-based preparation for Foundation Level certification.',
    'SQL for Testers': 'Database validation skills for functional, API, and automation testers.',
    'API Security Testing': 'Security testing fundamentals for APIs covering vulnerabilities and protection methods.',
    'Automation Framework Design Training': 'Framework architecture concepts required for Selenium, Playwright & Cypress.',

    // Power Platform
    'Power Apps Training': 'Build low-code applications for business workflows and data-driven interfaces.',
    'Power Automate Training': 'Create workflow automation for enterprise processes with low-code tools.',
    'Power BI Training': 'Dashboard creation, data modeling, and business insights with analytics visualizations.',

    // Mobile Development
    'React Native Training': 'Mobile app development for Android & iOS using JavaScript and component-driven UI.',
    'Flutter Training': 'Cross-platform mobile development using Dart and widget-based architecture.',
    'Android App Development (Basics)': 'Android fundamentals using UI components, activities, and basic app workflows.',

    // UI/UX Design
    'UI/UX Design Training': 'User research, interface design, information architecture, and modern design systems.',
    'Figma Design Training': 'Modern UI design using Figma for components, prototypes, and design systems.'
  };

  const courseOverview = courseOverviews[course] || `Master ${course} with our comprehensive training program. Get hands-on experience with real-world projects, expert mentorship, and 100% placement support.`;
  const courseSEO = {
    // ---------- Trending Courses ----------
    "Artificial Intelligence & Gen-AI": {
      title: "Best AI Courses in Chennai: Expert Training | Oranium Tech",
      description: "Learn AI in Chennai with expert trainers, practical projects & career-focused guidance. Explore generative AI courses & book a free demo today."
    },
    "Prompt Engineering": {
      title: "AI Prompt Engineering Training in Chennai | Oranium Tech",
      description: "Master prompt engineering with expert trainers & hands-on projects at Oranium Tech. Build practical AI skills & explore course options. Enroll today."
    },
    "Cloud Computing": {
      title: "Cloud Computing Training in Chennai | Oranium Tech",
      description: "Build cloud computing skills with expert trainers, practical labs & career guidance at Oranium Tech. Explore certification training & enroll today."
    },
    "DevOps & SRE": {
      title: "DevOps & SRE Course in Chennai | Oranium Tech ",
      description: "Learn DevOps & SRE with expert trainers, hands-on labs & real-time projects at Oranium Tech. Build job-ready skills & book a free demo today."
    },
    "Software Testing": {
      title: "Software Testing Course in Chennai | Oranium Tech",
      description: "Learn software testing with expert trainers, practical projects & industry-focused guidance at Oranium Tech. Explore the course & enroll today."
    },
    "Data Analytics": {
      title: "Data Analytics Training in Chennai | Oranium Tech",
      description: "Develop data analytics skills through practical projects & expert-led training at Oranium Tech. Explore career-focused learning & book a demo today."
    },
    "Data Engineering": {
      title: "Data Engineering Certification in Chennai | Oranium Tech",
      description: "Learn data engineering with expert trainers, practical projects & career-focused guidance at Oranium Tech. Build in-demand skills & enroll today."
    },
    "Full-Stack Development": {
      title: "Full-Stack Development Course in Chennai | Oranium Tech",
      description: "Train in full-stack development with expert mentors & hands-on projects at Oranium Tech. Build practical coding skills & book a free demo today."
    },
    "Power Platform / Low-Code Automation": {
      title: "Power Platform Low-Code Training in Chennai | Oranium Tech",
      description: "Learn Power Platform and low-code automation through expert-led training & practical projects at Oranium Tech. Explore the course & enroll today."
    },
    "Data Science": {
      title: "Data Science Course Training in Chennai | Oranium Tech",
      description: "Learn data science with expert trainers, hands-on projects & practical learning at Oranium Tech. Build analytical skills & book a free demo today."
    },
    "UI/UX Design": {
      title: "UI/UX Design Course in Chennai | Oranium Tech",
      description: "Develop UI/UX design skills with expert mentors & practical design projects at Oranium Tech. Learn industry tools & book a free demo today."
    },

    // ---------- Artificial Intelligence ----------
    "Data Science with Gen-AI Training": {
      title: "Data Science with Gen-AI Training in Chennai | Oranium Tech",
      description: "Learn data science & generative AI through expert-led lessons & hands-on projects at Oranium Tech. Build job-ready skills & enroll today."
    },
    "Generative AI Training": {
      title: "Generative AI Certification in Chennai | Oranium Tech",
      description: "Learn generative AI with expert trainers & hands-on projects at Oranium Tech. Develop practical AI skills & explore certification training today."
    },
    "Prompt Engineering Training": {
      title: "Prompt Engineering Course in Chennai | Oranium Tech",
      description: "Develop prompt engineering skills with expert trainers & practical AI projects at Oranium Tech. Explore the curriculum & book a free demo today."
    },
    "Agentic AI Training": {
      title: "Agentic AI Certification in Chennai | Oranium Tech",
      description: "Explore agentic AI with expert-led training & hands-on projects at Oranium Tech. Learn practical AI workflows & book a free demo today."
    },
    "Machine Learning Training": {
      title: "Machine Learning Course in Chennai | Oranium Tech",
      description: "Learn machine learning with expert trainers, practical projects & career-focused guidance at Oranium Tech. Build in-demand skills & enroll today."
    },
    "Microsoft Copilot Training": {
      title: "Microsoft Copilot Course in Chennai | Oranium Tech",
      description: "Learn Microsoft Copilot with expert trainers & practical AI workflows at Oranium Tech. Develop workplace skills & book a free demo today."
    },
    "AI in Project Management Training": {
      title: "AI in Project Management Course in Chennai | Oranium Tech",
      description: "Learn AI for project management with expert guidance & practical use cases at Oranium Tech. Explore smarter workflows & book a free demo today."
    },
    "AI in Excel Training": {
      title: "AI in Excel Training in Chennai | Oranium Tech",
      description: "Learn Excel automation with AI through practical exercises & expert-led training at Oranium Tech. Improve your workflow & enroll today."
    },
    "AI Business Analyst Training": {
      title: "AI Business Analyst Training in Chennai | Oranium Tech",
      description: "Develop business analysis skills with AI-focused training & practical projects at Oranium Tech. Explore the course & book a free demo today."
    },
    "AI Chatbot Development Training": {
      title: "AI Chatbot Development Course in Chennai | Oranium Tech",
      description: "Learn AI chatbot development with expert trainers & practical projects at Oranium Tech. Build conversational AI skills & enroll today."
    },

    // ---------- Software Testing ----------
    "Selenium Automation Training (Java/Python/C#)": {
      title: "Selenium Automation Training in Chennai | Oranium Tech",
      description: "Learn Selenium automation with practical testing projects & expert guidance at Oranium Tech. Explore training for automation testing & enroll today."
    },
    "Cypress Automation Training (JavaScript)": {
      title: "Cypress Automation Training in Chennai | Oranium Tech",
      description: "Learn Cypress automation with expert trainers & hands-on JavaScript testing projects at Oranium Tech. Strengthen testing skills & book a demo today."
    },
    "Playwright Automation Training (TypeScript)": {
      title: "Playwright Automation Course in Chennai | Oranium Tech",
      description: "Learn Playwright automation through hands-on testing projects & expert-led training at Oranium Tech. Build automation skills & enroll today."
    },
    "API Testing Training (Postman + RestAssured)": {
      title: "API Testing Training in Chennai | Oranium Tech",
      description: "Learn API testing with Postman & RestAssured through practical projects at Oranium Tech. Build testing skills with expert guidance & enroll today."
    },
    "Performance Testing Training (JMeter)": {
      title: "JMeter Performance Testing Course in Chennai | Oranium Tech ",
      description: "Learn performance testing with JMeter through hands-on exercises & expert training at Oranium Tech. Explore the curriculum & book a demo today."
    },
    "Mobile Automation Training (Appium)": {
      title: "Appium Mobile Automation Training in Chennai | Oranium Tech",
      description: "Learn Appium mobile automation with expert trainers & practical testing projects at Oranium Tech. Build job-ready skills & enroll today."
    },

    // ---------- Cloud Computing ----------
    "AWS Training": {
      title: "AWS Training in Chennai with Certification | Oranium Tech",
      description: "Learn AWS with expert trainers, hands-on cloud labs & practical projects at Oranium Tech. Develop cloud skills & book a free demo today."
    },
    "Azure Training": {
      title: "Best Azure Training in Chennai | Oranium Tech",
      description: "Learn Microsoft Azure through expert-led training, practical cloud labs & projects at Oranium Tech. Explore certification options & enroll today."
    },
    "GCP Training": {
      title: "Google Cloud Certification Course in Chennai | Oranium Tech",
      description: "Learn Google Cloud with expert trainers, practical labs & real-world projects at Oranium Tech. Build cloud skills & book a free demo today."
    },
    "Cloud Certification Training": {
      title: "Cloud Certification Training in Chennai | Oranium Tech",
      description: "Prepare for cloud certification with expert-led training & hands-on labs at Oranium Tech. Build practical cloud skills & explore courses today."
    },

    // ---------- DevOps ----------
    "DevOps Training": {
      title: "DevOps Training Institute in Chennai | Oranium Tech",
      description: "Learn DevOps with expert trainers, hands-on labs & real-time projects at Oranium Tech. Develop practical skills & book a free demo today."
    },
    "Linux Training": {
      title: "Linux Training Institute in Chennai | Oranium Tech",
      description: "Learn Linux commands, administration & practical troubleshooting with expert trainers at Oranium Tech. Build technical skills & enroll today."
    },
    "Git & GitHub Training": {
      title: "Git & GitHub Training in Chennai | Oranium Tech",
      description: "Learn Git & GitHub through practical version-control exercises & expert-led training at Oranium Tech. Strengthen your workflow & enroll today."
    },
    "CI/CD Jenkins Training": {
      title: "Jenkins CI/CD Training in Chennai | Oranium Tech",
      description: "Learn Jenkins & CI/CD pipelines through practical labs & expert-led training at Oranium Tech. Build DevOps skills & book a free demo today."
    },
    "Docker Training": {
      title: "Docker Training in Chennai: Hands-On Labs | Oranium Tech",
      description: "Learn Docker with expert trainers, containerization labs & practical projects at Oranium Tech. Build deployment skills & enroll today."
    },
    "Kubernetes Training": {
      title: "Kubernetes Certification Training in Chennai | Oranium Tech",
      description: "Learn Kubernetes with hands-on labs & expert guidance at Oranium Tech. Develop container orchestration skills & book a free demo today."
    },
    "Terraform Training": {
      title: "Terraform Training in Chennai: Practical Labs | Oranium Tech",
      description: "Learn Terraform infrastructure automation through hands-on labs & expert training at Oranium Tech. Build cloud automation skills & enroll today."
    },
    "Ansible Training": {
      title: "Ansible Certification Training in Chennai | Oranium Tech",
      description: "Learn Ansible automation with expert trainers & hands-on configuration labs at Oranium Tech. Build DevOps skills & book a free demo today."
    },
    "DevSecOps Training": {
      title: "DevSecOps Training in Chennai: Practical Labs | Oranium Tech",
      description: "Learn DevSecOps practices through practical security labs & expert-led training at Oranium Tech. Strengthen your skills & enroll today."
    },
    "Monitoring & Observability Training": {
      title: "Monitoring & Observability Course in Chennai | Oranium Tech ",
      description: "Learn monitoring & observability with practical labs & expert guidance at Oranium Tech. Explore modern monitoring tools & book a demo today."
    },

    // ---------- Web Development ----------
    "React Training": {
      title: "React JS Training in Chennai | Oranium Tech",
      description: "Learn React with expert trainers & hands-on frontend projects at Oranium Tech. Develop practical JavaScript skills & book a free demo today."
    },
    "MERN Full-Stack Training": {
      title: "MERN Stack Training in Chennai: Certification | Oranium Tech",
      description: "Learn MERN stack development with expert trainers & practical projects at Oranium Tech. Build full-stack skills & enroll today."
    },
    "Java Full-Stack Training": {
      title: "Java Full-Stack Developer Course in Chennai | Oranium Tech",
      description: "Learn Java full-stack development through practical projects & expert-led training at Oranium Tech. Build coding skills & book a demo today."
    },
    "Python Full-Stack Training": {
      title: "Python Full-Stack Training in Chennai | Oranium Tech",
      description: "Learn Python full-stack development through practical projects & expert-led training at Oranium Tech. Build job-ready skills & enroll today."
    },
    "DOT NET Full-Stack Training": {
      title: ".NET Full-Stack Training in Chennai | Oranium Tech",
      description: "Learn .NET full-stack development with expert trainers & practical projects at Oranium Tech. Build development skills & book a free demo today."
    },

    // ---------- Programming ----------
    "Java Programming": {
      title: "Java Programming Training in Chennai | Oranium Tech",
      description: "Learn Java programming through practical coding exercises & expert-led training at Oranium Tech. Build programming skills & enroll today."
    },
    "Python Programming": {
      title: "Python Programming Course in Chennai | Oranium Tech",
      description: "Learn Python programming with expert trainers, practical exercises & project-based learning at Oranium Tech. Build coding skills & enroll today."
    },
    "JavaScript Programming": {
      title: "JavaScript Course in Chennai | Oranium Tech",
      description: "Learn JavaScript through practical coding projects & expert-led training at Oranium Tech. Build frontend development skills & book a demo today."
    },
    "C Programming": {
      title: "C Programming Training in Chennai | Oranium Tech",
      description: "Learn C programming through hands-on coding exercises & expert-led training at Oranium Tech. Build programming fundamentals & enroll today."
    },
    "C++ Programming": {
      title: "C++ Programming Training in Chennai | Oranium Tech",
      description: "Learn C++ programming with expert trainers & practical coding exercises at Oranium Tech. Strengthen programming fundamentals & enroll today."
    },
    "Advanced Python Training": {
      title: "Advanced Python Training in Chennai | Oranium Tech",
      description: "Advance your Python skills with expert trainers, practical exercises & projects at Oranium Tech. Explore the course & book a free demo today."
    },
    "Python with Django Training": {
      title: "Python Django Training in Chennai | Oranium Tech",
      description: "Learn Django with Python through practical web development projects & expert-led training at Oranium Tech. Build skills & enroll today."
    },
    "Python with Flask Training": {
      title: "Python Flask Training in Chennai | Oranium Tech",
      description: "Learn Python Flask through practical web development projects & expert guidance at Oranium Tech. Build backend skills & book a free demo today."
    },

    // ---------- Data Engineering ----------
    "Data Engineering Training": {
      title: "Data Engineering Training in Chennai | Oranium Tech",
      description: "Learn data engineering through practical pipelines, expert instruction & real-world projects at Oranium Tech. Build in-demand skills & enroll today."
    },
    "Big Data Training": {
      title: "Big Data Training in Chennai | Oranium Tech",
      description: "Learn big data technologies through hands-on projects & expert-led training at Oranium Tech. Build data skills & book a free demo today."
    },
    "Cloud Data Engineering Training": {
      title: "Cloud Data Engineering Course in Chennai | Oranium Tech",
      description: "Learn cloud data engineering with expert trainers & practical pipeline projects at Oranium Tech. Develop cloud data skills & enroll today."
    },

    // ---------- Testing Add-ons ----------
    "ISTQB Certification Training": {
      title: "ISTQB Certification Training in Chennai | Oranium Tech",
      description: "Prepare for ISTQB certification with expert guidance & practical testing concepts at Oranium Tech. Strengthen your QA skills & enroll today."
    },
    "SQL for Testers": {
      title: "SQL for Testers Course in Chennai | Oranium Tech",
      description: "Learn SQL for software testing through practical database exercises & expert-led training at Oranium Tech. Strengthen QA skills & enroll today."
    },
    "API Security Testing": {
      title: "API Security Testing Training in Chennai | Oranium Tech",
      description: "Learn API security testing with practical exercises & expert guidance at Oranium Tech. Develop security testing skills & book a demo today."
    },
    "Automation Framework Design Training": {
      title: "Automation Framework Design Course in Chennai | Oranium Tech",
      description: "Learn automation framework design through practical testing projects & expert-led training at Oranium Tech. Build QA skills & enroll today."
    },

    // ---------- Power Platform ----------
    "Power Apps Training": {
      title: "Power Apps Training Institute in Chennai | Oranium Tech",
      description: "Learn Microsoft Power Apps through hands-on low-code projects & expert-led training at Oranium Tech. Build app development skills & enroll today."
    },
    "Power Automate Training": {
      title: "Power Automate Training in Chennai | Oranium Tech",
      description: "Learn Power Automate through practical workflow automation projects & expert guidance at Oranium Tech. Improve productivity & enroll today."
    },
    "Power BI Training": {
      title: "Power BI Training Institute in Chennai | Oranium Tech",
      description: "Learn Power BI through practical dashboards, data modelling & expert-led training at Oranium Tech. Build analytics skills & book a demo today."
    },

    // ---------- Mobile Development ----------
    "React Native Training": {
      title: "React Native Training in Chennai | Oranium Tech",
      description: "Learn React Native through practical mobile app projects & expert-led training at Oranium Tech. Build cross-platform development skills & enroll today."
    },
    "Flutter Training": {
      title: "Flutter App Development Course in Chennai | Oranium Tech",
      description: "Learn Flutter through hands-on mobile app projects & expert guidance at Oranium Tech. Build cross-platform development skills & enroll today."
    },
    "Android App Development (Basics)": {
      title: "Android App Development Course in Chennai | Oranium Tech",
      description: "Learn android app development through practical exercises & expert-led training at Oranium Tech. Build mobile development skills & enroll today."
    },

    // ---------- UI/UX Design ----------
    "UI/UX Design Training": {
      title: "UI/UX Design Training with Projects | Oranium Tech",
      description: "Learn UI/UX design with practical projects & expert guidance at Oranium Tech. Develop portfolio-ready skills & book a free demo today."
    },
    "Figma Design Training": {
      title: "Figma Design Training in Chennai | Oranium Tech",
      description: "Learn Figma with expert trainers & hands-on interface design projects at Oranium Tech. Develop practical design skills & book a free demo today."
    }
  };
  const normalizeKey = (s = '') =>
    s.toLowerCase()
      .replace(/[^a-z0-9]/g, '')        // spaces, hyphens, &, /, ( ) sab hata do
      .replace(/^dotnet/, 'net');       // "DOT NET" aur ".NET" dono same ho jayein

  const seoLookup = Object.fromEntries(
    Object.entries(courseSEO).map(([name, value]) => [normalizeKey(name), value])
  );

  const buildCourseSEO = (course, overview, duration) => {
    const custom = seoLookup[normalizeKey(course)];
    if (custom) return custom;

    console.warn('SEO not found for course:', JSON.stringify(course));

    const baseName = course.replace(/\s*Training$/i, '');
    return {
      title: `${baseName} Course in Chennai | Oranium Tech`,
      description: `${overview} ${duration} course with live projects, certification and placement support at Oranium Tech, Velachery & Chrompet, Chennai.`.slice(0, 160)
    };
  };
  const seo = buildCourseSEO(course, courseOverview, courseDuration);
  return (
    <div ref={heroRef} style={{ backgroundColor: '#f8f9fa' }}>
      <SEO
        title={seo.title}
        description={seo.description}
      />
      <Helmet>
        <title>{course} - Oranium Tech | IT Training Chennai</title>
        <meta name="description" content={`Learn ${course} at Oranium Tech Chennai. Expert training with placement support in Velachery & Chrompet.`} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Course",
            "name": course,
            "description": courseOverview,
            "provider": {
              "@type": "EducationalOrganization",
              "name": "Oranium Tech",
              "url": "https://oraniumtech.com",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Chennai",
                "addressRegion": "Tamil Nadu",
                "addressCountry": "IN"
              }
            },
            "courseMode": ["Online", "Onsite"],
            "educationalLevel": "Beginner to Advanced",
            "timeRequired": courseDuration,
            "hasCourseInstance": [
              {
                "@type": "CourseInstance",
                "courseMode": "Onsite",
                "location": {
                  "@type": "Place",
                  "name": "Oranium Tech Velachery",
                  "address": {
                    "@type": "PostalAddress",
                    "streetAddress": "52, Ponniamman Complex, 2nd Floor, Velachery Main Rd",
                    "addressLocality": "Chennai",
                    "postalCode": "600042",
                    "addressCountry": "IN"
                  }
                }
              },
              {
                "@type": "CourseInstance",
                "courseMode": "Onsite",
                "location": {
                  "@type": "Place",
                  "name": "Oranium Tech Chrompet",
                  "address": {
                    "@type": "PostalAddress",
                    "streetAddress": "9, 2nd Floor, 14th cross street, New Colony",
                    "addressLocality": "Chennai",
                    "postalCode": "600044",
                    "addressCountry": "IN"
                  }
                }
              }
            ],
            "offers": {
              "@type": "Offer",
              "category": "Paid",
              "availability": "https://schema.org/InStock"
            }
          })}
        </script>
      </Helmet>
      <Header
        onCourseSelect={onCourseSelect}
        onEnrollClick={onEnrollClick}
        activePage={activePage}
        onHomeClick={onHomeClick}
        onAboutClick={onAboutClick}
        onContactClick={onContactClick}
        selectedCourse={selectedCourse}
        onMobileMenuToggle={handleMobileMenuToggle}
      />
      {/* Fade-in keyframes */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .fade-in-section {
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 0.8s ease-out, transform 0.8s ease-out;
        }
        .fade-in-section.fade-in-visible {
          opacity: 1;
          transform: translateY(0);
        }
        .hero-fade-in {
          animation: fadeInUp 1s ease-out forwards;
        }
      `}</style>

      {/* Hero Section */}
      <section className="hero-fade-in" style={{
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0.25)), url(${coursesBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        color: 'white',
        padding: '0rem 2rem 7rem',
        paddingTop: '0px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Curved bottom edge - smooth wave */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '100%',
          overflow: 'hidden',
          lineHeight: 0
        }}>
          <svg viewBox="0 0 1440 80" preserveAspectRatio="none" style={{
            width: '100%',
            height: '80px',
            display: 'block'
          }}>
            <path d="M0,0 Q720,80 1440,0 L1440,80 L0,80 Z"
              fill="#f8f9fa" />
          </svg>
        </div>

        <div style={{
          position: 'relative',
          zIndex: 1,
          maxWidth: '1200px',
          margin: '0 auto',
          textAlign: 'center',
          paddingTop: '6rem'
        }}>
          <h1 style={{
            fontSize: 'clamp(2.2rem, 5vw, 3.5rem)',
            marginBottom: '1.5rem',
            fontWeight: '700',
            lineHeight: '1.2',
            textShadow: '2px 2px 8px rgba(0,0,0,0.3)'
          }}>
            {course}
          </h1>
          <p style={{
            fontSize: 'clamp(1.05rem, 2vw, 1.25rem)',
            lineHeight: '1.7',
            maxWidth: '900px',
            margin: '0 auto 2.5rem',
            opacity: 0.98,
            fontWeight: '400',
            textShadow: '1px 1px 4px rgba(0,0,0,0.2)'
          }}>
            {courseOverview}
          </p>

          <div style={{
            display: 'flex',
            gap: '1.2rem',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <button
              onClick={() => setShowEnrollModal(true)}
              style={{
                background: 'linear-gradient(135deg, #0066cc 0%, #0052a3 100%)',
                color: 'white',
                padding: '0.95rem 2.2rem',
                border: 'none',
                borderRadius: '30px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 15px rgba(0,102,204,0.4)',
                transition: 'all 0.3s ease',
                textTransform: 'none'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(0,102,204,0.5)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 15px rgba(0,102,204,0.4)';
              }}
            >
              Enroll Now
            </button>
            <button
              onClick={() => setShowSyllabusModal(true)}
              style={{
                background: 'linear-gradient(135deg, #FF6B35 0%, #FF8C42 100%)',
                color: 'white',
                border: 'none',
                padding: '0.95rem 2.2rem',
                borderRadius: '30px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 15px rgba(255,107,53,0.4)',
                transition: 'all 0.3s ease',
                textTransform: 'none'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(255,107,53,0.5)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 15px rgba(255,107,53,0.4)';
              }}
            >
              Unlock Syllabus via Email
            </button>
          </div>
        </div>
      </section>

      {/* Course Highlights */}
      <section className="fade-in-section" style={{
        padding: '5rem 2rem',
        background: '#ffffff'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {/* Centered Title */}
          <h2 style={{
            fontSize: '2.5rem',
            color: '#003d7a',
            marginBottom: '3.5rem',
            fontWeight: '700',
            lineHeight: '1.2',
            textAlign: 'center',
            position: 'relative',
            display: 'inline-block',
            width: '100%'
          }}>
            Course Highlights
            <div style={{
              position: 'absolute',
              bottom: '-10px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '280px',
              height: '4px',
              background: 'linear-gradient(90deg, #ff6b35 0%, #ffa500 100%)',
              borderRadius: '2px'
            }}></div>
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '42% 58%',
            gap: '4rem',
            alignItems: 'center'
          }}>
            {/* Left Image with Smooth Perspective */}
            <div
              style={{
                position: 'relative',
                transition: 'transform 0.4s ease',
                transformStyle: 'preserve-3d',
                cursor: 'pointer',
                marginTop: '2rem'
              }}
              onMouseMove={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const y = e.clientY - rect.top;
                const centerX = rect.width / 2;
                const centerY = rect.height / 2;
                const rotateX = ((y - centerY) / centerY) * -10;
                const rotateY = ((x - centerX) / centerX) * 10;
                e.currentTarget.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
              }}
            >
              <img
                src={computerImg}
                alt="IT training courses at Oranium Tech Chennai"
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  borderRadius: '8px'
                }}
              />
            </div>

            {/* Right Content - Zigzag Cards */}
            <div style={{
              flex: '1 1 280px',
              minWidth: '250px',
              maxWidth: '380px',
              display: 'flex',
              flexDirection: 'row',
              gap: '1.5rem',
              position: 'relative',
              zIndex: 1,
              alignItems: 'flex-start',
              justifyContent: 'center',
              marginRight: '-4rem'
            }}>
              {/* Left Column */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1.5rem',
                alignItems: 'center'
              }}>
                {/* Placement Assurance */}
                <div style={{
                  background: 'linear-gradient(145deg, #ffffff, #f8f9ff)',
                  borderRadius: '16px',
                  padding: '1.8rem 1.5rem',
                  textAlign: 'center',
                  boxShadow: '8px 8px 16px #b8bec7, -8px -8px 16px #ffffff',
                  transform: 'translateY(0) translateZ(0)',
                  transition: '0.3s',
                  position: 'relative',
                  overflow: 'visible',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  width: '220px'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '-3px',
                    left: '-3px',
                    width: '100px',
                    height: '3px',
                    background: 'linear-gradient(90deg, #3b82f6, #1e40af)',
                    borderRadius: '2px'
                  }}></div>
                  <div style={{
                    position: 'absolute',
                    top: '-3px',
                    left: '-3px',
                    width: '3px',
                    height: '100px',
                    background: 'linear-gradient(#3b82f6, #1e40af)',
                    borderRadius: '2px'
                  }}></div>
                  <div style={{ position: 'relative', zIndex: 1 }}>
                    <div style={{
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: '700',
                      fontSize: '1.05rem',
                      color: '#00315d',
                      letterSpacing: '0.3px',
                      marginBottom: '0.8rem'
                    }}>Placement Assurance</div>
                    <div style={{
                      color: '#007bff',
                      fontWeight: '600',
                      fontSize: '2.5rem',
                      fontFamily: 'Manrope, sans-serif',
                      textShadow: '0 2px 4px rgba(0, 123, 255, 0.1)'
                    }}>100%</div>
                  </div>
                </div>

                {/* Certification */}
                <div style={{
                  background: 'linear-gradient(145deg, #ffffff, #f5fff5)',
                  borderRadius: '16px',
                  padding: '1.8rem 1.5rem',
                  textAlign: 'center',
                  boxShadow: '8px 8px 16px #b8bec7, -8px -8px 16px #ffffff',
                  transform: 'translateY(0) translateZ(0)',
                  transition: '0.3s',
                  position: 'relative',
                  overflow: 'hidden',
                  border: '2px solid rgba(0, 200, 83, 0.2)',
                  width: '220px'
                }}>
                  <div style={{ position: 'relative', zIndex: 1 }}>
                    <div style={{
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: '700',
                      fontSize: '1.05rem',
                      color: '#00315d',
                      marginBottom: '0.8rem',
                      letterSpacing: '0.3px'
                    }}>Certification</div>
                    <div style={{
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: '600',
                      color: '#00c853',
                      fontSize: '2.5rem',
                      textShadow: '0 2px 4px rgba(0, 200, 83, 0.1)'
                    }}>Yes</div>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1.5rem',
                alignItems: 'center',
                marginTop: '3rem'
              }}>
                {/* Expert Support */}
                <div style={{
                  background: 'linear-gradient(145deg, #ffffff, #fff8f5)',
                  borderRadius: '16px',
                  padding: '1.8rem 1.5rem',
                  textAlign: 'center',
                  transform: 'translateY(0) translateZ(0)',
                  boxShadow: '8px 8px 16px #b8bec7, -8px -8px 16px #ffffff',
                  transition: '0.3s',
                  position: 'relative',
                  overflow: 'hidden',
                  border: '2px solid rgba(255, 107, 53, 0.2)',
                  width: '220px'
                }}>
                  <div style={{ position: 'relative', zIndex: 1 }}>
                    <div style={{
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: '700',
                      fontSize: '1.05rem',
                      color: '#00315d',
                      letterSpacing: '0.3px',
                      marginBottom: '0.8rem'
                    }}>Expert Support</div>
                    <div style={{
                      color: '#ff6b35',
                      fontWeight: '600',
                      fontSize: '2.5rem',
                      fontFamily: 'Manrope, sans-serif',
                      textShadow: '0 2px 4px rgba(255, 107, 53, 0.1)'
                    }}>24/7</div>
                  </div>
                </div>

                {/* Duration */}
                <div style={{
                  background: 'linear-gradient(145deg, #ffffff, #fff5f8)',
                  borderRadius: '16px',
                  padding: '1.8rem 1.5rem',
                  textAlign: 'center',
                  transform: 'translateY(0) translateZ(0)',
                  boxShadow: '8px 8px 16px #b8bec7, -8px -8px 16px #ffffff',
                  transition: '0.3s',
                  position: 'relative',
                  overflow: 'visible',
                  border: '2px solid rgba(139, 0, 139, 0.2)',
                  width: '220px'
                }}>
                  <div style={{
                    position: 'absolute',
                    bottom: '-3px',
                    right: '-3px',
                    width: '80px',
                    height: '3px',
                    background: 'linear-gradient(90deg, #8b008b, #ff1493)',
                    borderRadius: '2px'
                  }}></div>
                  <div style={{
                    position: 'absolute',
                    bottom: '-3px',
                    right: '-3px',
                    width: '3px',
                    height: '80px',
                    background: 'linear-gradient(#ff1493, #8b008b)',
                    borderRadius: '2px'
                  }}></div>
                  <div style={{ position: 'relative', zIndex: 1 }}>
                    <div style={{
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: '700',
                      fontSize: '1.05rem',
                      color: '#00315d',
                      marginBottom: '0.8rem',
                      letterSpacing: '0.3px'
                    }}>Duration</div>
                    <div style={{
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: '600',
                      color: '#8b008b',
                      fontSize: '1.3rem',
                      textShadow: '0 2px 4px rgba(139, 0, 139, 0.1)',
                      lineHeight: '1.3'
                    }}>{courseDuration}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Responsive Styles */}
        <style>{`
          @media (max-width: 968px) {
            section > div > div:last-child {
              grid-template-columns: 1fr !important;
            }
          }
          @media (max-width: 768px) {
            section > div > div:nth-child(2) > div:first-child {
              order: 2;
              margin-top: 2rem !important;
            }
            section > div > div:nth-child(2) > div:last-child {
              order: 1;
              flex-direction: column !important;
              margin-right: 0 !important;
            }
            section > div > div:nth-child(2) > div:last-child > div {
              margin-top: 0 !important;
            }
          }
        `}</style>
      </section>

      {/* Training Benefits - Section 3 */}
      <section className="fade-in-section" style={{
        padding: '3rem 2rem',
        background: 'linear-gradient(135deg, #001529 0%, #002140 50%, #003366 100%)',
        boxShadow: 'inset 0 -15px 30px rgba(0, 0, 0, 0.5)',
        overflow: 'hidden'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <div className="training-benefits-grid" style={{
            display: 'grid',
            gridTemplateColumns: '55% 45%',
            gap: '3rem',
            alignItems: 'center'
          }}>
            {/* Left Content */}
            <div>
              <h2 style={{
                fontSize: '2.5rem',
                color: '#ffffff',
                marginBottom: '1rem',
                fontWeight: '700',
                lineHeight: '1.2',
                textAlign: 'left'
              }}>
                Training Benefits - {course}
              </h2>

              <p style={{
                fontSize: '1rem',
                color: '#b8bac9',
                lineHeight: '1.8',
                marginBottom: '2rem',
                textAlign: 'left'
              }}>
                OraniumTech provides industry-aligned {course} in Chennai to help you gain the top skills required for a successful career. This training program is delivered by expert professionals with real-time, practical sessions designed to match actual IT project workflows. You will master professional skills, receive certification guidance, and get placement assistance at the end of your training.
              </p>

              {/* Stats without progress bars */}
              {courseStats[course] && (
                <div style={{
                  display: 'flex',
                  gap: '2.5rem',
                  alignItems: 'flex-start',
                  marginBottom: '2.5rem',
                  justifyContent: 'flex-start'
                }}>
                  {/* Placement */}
                  <div style={{ textAlign: 'left' }}>
                    <div style={{
                      color: '#ffffff',
                      fontSize: '2rem',
                      fontWeight: '700',
                      marginBottom: '0.3rem',
                      lineHeight: '1'
                    }}>{courseStats[course].placement}</div>
                    <div style={{
                      color: '#b8bac9',
                      fontSize: '0.85rem',
                      fontWeight: '500',
                      lineHeight: '1.3',
                      maxWidth: '150px',
                      textAlign: 'left'
                    }}>Chances of immediate placements</div>
                  </div>

                  {/* Salary */}
                  <div style={{ textAlign: 'left' }}>
                    <div style={{
                      color: '#ffffff',
                      fontSize: '2rem',
                      fontWeight: '700',
                      marginBottom: '0.3rem',
                      lineHeight: '1'
                    }}>{courseStats[course].salary}</div>
                    <div style={{
                      color: '#b8bac9',
                      fontSize: '0.85rem',
                      fontWeight: '500',
                      lineHeight: '1.3',
                      maxWidth: '150px',
                      textAlign: 'left'
                    }}>Increase in salary</div>
                  </div>

                  {/* Market */}
                  <div style={{ textAlign: 'left' }}>
                    <div style={{
                      color: '#ffffff',
                      fontSize: '2rem',
                      fontWeight: '700',
                      marginBottom: '0.3rem',
                      lineHeight: '1'
                    }}>{courseStats[course].market}</div>
                    <div style={{
                      color: '#b8bac9',
                      fontSize: '0.85rem',
                      fontWeight: '500',
                      lineHeight: '1.3',
                      maxWidth: '150px',
                      textAlign: 'left'
                    }}>Of job market is open</div>
                  </div>
                </div>
              )}

              {/* Book a Free Demo Button */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  setShowEnrollModal(true);
                }}
                style={{
                  background: 'linear-gradient(135deg, #ff6b35 0%, #ff8c42 50%, #ffad5a 100%)',
                  color: 'white',
                  border: '2px solid rgba(255,255,255,0.2)',
                  padding: '1rem 2rem',
                  borderRadius: '25px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '600',
                  boxShadow: 'inset 0 2px 4px rgba(255,255,255,0.3), inset 0 -2px 4px rgba(0,0,0,0.2), 0 8px 16px rgba(255,107,53,0.3)',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  marginTop: '0.5rem'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.05) translateY(-2px)';
                  e.target.style.boxShadow = 'inset 0 1px 2px rgba(255,255,255,0.4), inset 0 -1px 2px rgba(0,0,0,0.3), 0 12px 24px rgba(255,107,53,0.4)';
                  e.target.style.background = 'linear-gradient(135deg, #ff8c42 0%, #ffad5a 50%, #ffc971 100%)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1) translateY(0px)';
                  e.target.style.boxShadow = 'inset 0 2px 4px rgba(255,255,255,0.3), inset 0 -2px 4px rgba(0,0,0,0.2), 0 8px 16px rgba(255,107,53,0.3)';
                  e.target.style.background = 'linear-gradient(135deg, #ff6b35 0%, #ff8c42 50%, #ffad5a 100%)';
                }}
              >
                Book a Free Demo
              </button>
            </div>

            {/* Right Image */}
            <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <img
                src={frameImg}
                alt="Professional IT training benefits at Oranium Tech"
                style={{
                  width: '85%',
                  height: 'auto',
                  display: 'block'
                }}
              />
            </div>
          </div>
        </div>

        {/* Responsive Styles */}
        <style>{`
          @media (max-width: 968px) {
            section > div > div {
              grid-template-columns: 1fr !important;
            }
          }
        `}</style>
      </section>

      {/* Course Modules */}
      <section className="fade-in-section" style={{
        padding: '5rem 2rem',
        background: 'linear-gradient(135deg, #7db3f5 0%, #a8cef7 50%, #7db3f5 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background decoration - enhanced geometric shapes */}
        <div style={{
          position: 'absolute',
          top: '-80px',
          right: '-80px',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'rgba(59, 130, 246, 0.12)',
          pointerEvents: 'none'
        }}></div>
        <div style={{
          position: 'absolute',
          bottom: '-100px',
          left: '-100px',
          width: '280px',
          height: '280px',
          borderRadius: '50%',
          background: 'rgba(147, 197, 253, 0.15)',
          pointerEvents: 'none'
        }}></div>
        <div style={{
          position: 'absolute',
          top: '35%',
          right: '8%',
          width: '150px',
          height: '150px',
          borderRadius: '50%',
          background: 'rgba(191, 219, 254, 0.25)',
          pointerEvents: 'none'
        }}></div>
        <div style={{
          position: 'absolute',
          bottom: '25%',
          left: '6%',
          width: '180px',
          height: '180px',
          borderRadius: '50%',
          background: 'rgba(147, 197, 253, 0.18)',
          pointerEvents: 'none'
        }}></div>

        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <h2 style={{
            fontSize: '2.5rem',
            color: '#003d7a',
            marginBottom: '3.5rem',
            textAlign: 'center',
            fontWeight: '700',
            position: 'relative',
            display: 'inline-block',
            width: '100%'
          }}>
            Course Modules
            <div style={{
              position: 'absolute',
              bottom: '-10px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '200px',
              height: '4px',
              background: 'linear-gradient(90deg, #ff6b35 0%, #ffa500 100%)',
              borderRadius: '2px'
            }}></div>
          </h2>
          <div className="course-modules-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '1.2rem',
            maxWidth: '1100px',
            margin: '0 auto'
          }}>
            {modules.map((module, index) => (
              <div
                key={index}
                className="module-item"
                style={{
                  background: 'linear-gradient(145deg, #ffffff 0%, #f8faff 50%, #f0f7ff 100%)',
                  borderRadius: '14px',
                  boxShadow: '0 8px 25px rgba(59, 130, 246, 0.25), 0 4px 12px rgba(59, 130, 246, 0.2), 0 2px 6px rgba(0, 49, 93, 0.1), inset 0 3px 6px rgba(255, 255, 255, 1), inset 0 -3px 6px rgba(59, 130, 246, 0.08)',
                  padding: '1.2rem 1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  fontSize: '1.05rem',
                  color: '#00315d',
                  fontWeight: '600',
                  border: '2px solid rgba(59, 130, 246, 0.3)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  minHeight: '70px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 12px 35px rgba(59, 130, 246, 0.3), 0 6px 16px rgba(59, 130, 246, 0.25), 0 3px 8px rgba(0, 49, 93, 0.15), inset 0 3px 6px rgba(255, 255, 255, 1), inset 0 -3px 6px rgba(59, 130, 246, 0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(59, 130, 246, 0.25), 0 4px 12px rgba(59, 130, 246, 0.2), 0 2px 6px rgba(0, 49, 93, 0.1), inset 0 3px 6px rgba(255, 255, 255, 1), inset 0 -3px 6px rgba(59, 130, 246, 0.08)';
                }}
              >
                <div className="module-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" style={{ display: 'block', flexShrink: 0 }}>
                    <circle cx="12" cy="12" r="10" stroke="#ffffff" strokeWidth="2.5"></circle>
                    <path d="M8 12l3 3 5-5" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"></path>
                  </svg>
                </div>
                <span className="module-text" style={{ flex: 1, lineHeight: '1.4', paddingTop: '2px' }}>{module}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Responsive Styles */}
        <style>{`
          .module-icon {
            background: linear-gradient(135deg, #ff6b35, #ffa500);
            border-radius: 50%;
            padding: 6px;
            display: flex;
            align-items: center;
            justify-content: center;
            min-width: 36px;
            width: 36px;
            height: 36px;
            flex-shrink: 0;
            box-shadow: 0 3px 8px rgba(255, 107, 53, 0.3);
            margin-top: 2px;
          }
          
          @media (max-width: 768px) {
            section > div > div:last-child {
              grid-template-columns: 1fr !important;
            }
          }
        `}</style>
      </section>

      {/* Certificate Section - Section 5 */}
      <section className="fade-in-section" style={{
        padding: '3rem 2rem',
        background: '#f0f7ff',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <div className="certificate-section-grid" style={{
            display: 'grid',
            gridTemplateColumns: '50% 50%',
            gap: '4rem',
            alignItems: 'center'
          }}>
            {/* Left Content */}
            <div>
              <h2 style={{
                fontSize: '2.2rem',
                color: '#001f3f',
                marginBottom: '1.5rem',
                fontWeight: '700',
                lineHeight: '1.2'
              }}>
                Get Industry Recognized<br />Certification
              </h2>

              <p style={{
                fontSize: '1.1rem',
                color: '#2563eb',
                lineHeight: '1.8',
                marginBottom: '2.5rem',
                fontWeight: '500'
              }}>
                This certification validates the learner's expertise in {course.toLowerCase()}, covering industry-relevant curriculum and practical approaches. This certification is widely recognized by top IT companies.
              </p>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1.2rem'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.8rem'
                }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #ff6b35 0%, #ffa500 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <svg width="14" height="11" viewBox="0 0 14 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 5.5L5 9.5L13 1.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <span style={{
                    color: '#374151',
                    fontSize: '1rem',
                    fontWeight: '500'
                  }}>Strong foundation in core concepts</span>
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.8rem'
                }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #ff6b35 0%, #ffa500 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <svg width="14" height="11" viewBox="0 0 14 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 5.5L5 9.5L13 1.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <span style={{
                    color: '#374151',
                    fontSize: '1rem',
                    fontWeight: '500'
                  }}>Industry-recognized certification</span>
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.8rem'
                }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #ff6b35 0%, #ffa500 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <svg width="14" height="11" viewBox="0 0 14 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 5.5L5 9.5L13 1.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <span style={{
                    color: '#374151',
                    fontSize: '0.95rem',
                    fontWeight: '500'
                  }}>Hands-on practical experience</span>
                </div>
              </div>
            </div>

            {/* Right Image with decorative frame */}
            <div style={{
              flex: '1 1 44%',
              position: 'relative',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '580px'
            }}>
              {/* Top left decorative box */}
              <div style={{
                position: 'absolute',
                top: '40px',
                left: '-40px',
                width: '120px',
                height: '120px',
                backgroundColor: '#003d82',
                transform: 'skewX(-18deg)',
                zIndex: 1,
                opacity: 0.9
              }}></div>

              {/* Bottom right decorative box */}
              <div style={{
                position: 'absolute',
                bottom: '60px',
                right: '-30px',
                width: '100px',
                height: '100px',
                backgroundColor: '#003d82',
                transform: 'skewX(-18deg)',
                zIndex: 1,
                opacity: 0.85
              }}></div>

              {/* Circular ring */}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '480px',
                height: '480px',
                border: '2px solid rgba(0, 61, 130, 0.6)',
                borderRadius: '50%',
                zIndex: 1
              }}></div>

              {/* Certificate card frame */}
              <div style={{
                position: 'relative',
                width: '360px',
                height: '520px',
                background: 'linear-gradient(135deg, #003d82 0%, #0052a3 100%)',
                borderRadius: '16px',
                padding: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
                zIndex: 2
              }}>
                <div style={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: '#ffffff',
                  borderRadius: '12px',
                  padding: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1) inset'
                }}>
                  <img
                    src={certificateImg}
                    alt="Industry-recognized IT training certificate from Oranium Tech Chennai"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      borderRadius: '8px'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Responsive Styles */}
        <style>{`
          @media (max-width: 968px) {
            section > div > div {
              grid-template-columns: 1fr !important;
            }
          }
        `}</style>
      </section>

      {/* Related Courses Section */}
      <section className="fade-in-section" style={{
        padding: '3rem 2rem',
        background: '#ffffff',
        position: 'relative'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          {/* Section Title */}
          <h2 style={{
            fontSize: '2.5rem',
            color: '#003d7a',
            textAlign: 'center',
            marginBottom: '1rem',
            fontWeight: '700'
          }}>
            Related Courses
          </h2>
          <div style={{
            width: '280px',
            height: '4px',
            background: 'linear-gradient(90deg, #ff6b35 0%, #ffa500 100%)',
            margin: '0 auto 3rem',
            borderRadius: '2px'
          }}></div>

          {/* Carousel Container */}
          <div style={{ position: 'relative', marginTop: '3rem' }}>
            {/* Left Arrow */}
            {carouselIndex > 0 && (
              <button
                className="carousel-nav-button"
                onClick={() => setCarouselIndex(Math.max(0, carouselIndex - 1))}
                style={{
                  position: 'absolute',
                  left: '-20px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '45px',
                  height: '45px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-50%) scale(1.1)';
                  e.target.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(-50%) scale(1)';
                  e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
                }}
              >
                ‹
              </button>
            )}

            {/* Right Arrow */}
            {carouselIndex < relatedCourses.length - 4 && (
              <button
                className="carousel-nav-button"
                onClick={() => setCarouselIndex(Math.min(relatedCourses.length - 4, carouselIndex + 1))}
                style={{
                  position: 'absolute',
                  right: '-20px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '45px',
                  height: '45px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-50%) scale(1.1)';
                  e.target.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(-50%) scale(1)';
                  e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
                }}
              >
                ›
              </button>
            )}

            {/* Courses Wrapper */}
            <div ref={carouselRef} className="related-courses-carousel" style={{
              overflow: 'hidden',
              padding: '0 10px'
            }}>
              <div style={{
                display: 'flex',
                gap: '1.5rem',
                transform: `translateX(calc(-${carouselIndex} * (25% + 1.5rem)))`,
                transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
              }}>
                {relatedCourses.map((relatedCourse, index) => {
                  const relatedOverview = courseOverviews[relatedCourse] || `Master ${relatedCourse} with our comprehensive training program.`;

                  return (
                    <div
                      key={index}
                      style={{
                        minWidth: 'calc(25% - 1.125rem)',
                        background: '#ffffff',
                        borderRadius: '18px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 6px 22px rgba(0, 0, 0, 0.12)',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        height: '320px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-8px)';
                        e.currentTarget.style.boxShadow = '0 12px 32px rgba(0, 0, 0, 0.18)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 6px 22px rgba(0, 0, 0, 0.12)';
                      }}
                    >
                      {/* Header */}
                      <div style={{
                        background: 'linear-gradient(135deg, rgb(15, 23, 42) 0%, rgb(30, 39, 68) 100%)',
                        padding: '22px 24px',
                        color: '#ffffff',
                        height: '160px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <div style={{
                          fontSize: '22px',
                          fontWeight: '700',
                          lineHeight: '1.2',
                          flex: 1
                        }}>
                          {relatedCourse}
                        </div>
                        <svg width="86" height="86" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ minWidth: '86px' }}>
                          <circle cx="50" cy="50" r="45" fill="rgba(255,255,255,0.1)" stroke="rgba(255,255,255,0.3)" strokeWidth="2"></circle>
                          <path d="M30 50 L45 65 L70 35" stroke="#3b82f6" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"></path>
                          <circle cx="50" cy="50" r="8" fill="#3b82f6"></circle>
                          <path d="M50 25 L50 30 M50 70 L50 75 M25 50 L30 50 M70 50 L75 50" stroke="rgba(255,255,255,0.5)" strokeWidth="2" strokeLinecap="round"></path>
                        </svg>
                      </div>

                      {/* Content */}
                      <div style={{
                        padding: '22px 24px',
                        fontSize: '15px',
                        color: '#333333',
                        lineHeight: '1.6',
                        height: '160px',
                        display: 'flex',
                        flexDirection: 'column'
                      }}>
                        <div style={{
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical'
                        }}>
                          {relatedOverview}
                        </div>
                        <div
                          onClick={(e) => {
                            console.log('Know More clicked:', relatedCourse);
                            console.log('onCourseSelect exists:', !!onCourseSelect);
                            if (onCourseSelect) {
                              onCourseSelect(relatedCourse);
                            } else {
                              alert('onCourseSelect not defined');
                            }
                          }}
                          style={{
                            display: 'inline-block',
                            marginTop: '16px',
                            fontWeight: '600',
                            color: 'rgb(15, 23, 48)',
                            fontSize: '15px',
                            cursor: 'pointer',
                            userSelect: 'none'
                          }}>
                          Know More
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ marginLeft: '6px', verticalAlign: 'middle' }}>
                            <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                          </svg>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enrollment Modal */}
      {showEnrollModal && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '1rem',
            overflow: 'auto'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowEnrollModal(false);
          }}
        >
          <div className="modal-content" style={{
            background: 'white',
            borderRadius: '16px',
            padding: '0',
            maxWidth: '420px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            position: 'relative',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            overflow: 'hidden'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #001f3f 0%, #003366 100%)',
              padding: '3rem 2rem',
              position: 'relative'
            }}>
              <button
                onClick={() => setShowEnrollModal(false)}
                style={{
                  position: 'absolute',
                  top: '0.5rem',
                  right: '0.5rem',
                  background: 'rgba(255,255,255,0.3)',
                  border: 'none',
                  fontSize: '1.8rem',
                  cursor: 'pointer',
                  color: 'white',
                  lineHeight: 1,
                  fontWeight: '400',
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease',
                  zIndex: 10
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255,255,255,0.5)';
                  e.target.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255,255,255,0.3)';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                ×
              </button>
              <h2 style={{
                color: 'white',
                margin: 0,
                fontSize: '1.6rem',
                fontWeight: '600',
                textAlign: 'center',
                lineHeight: '1.5'
              }}>
                Enroll in <span style={{
                  background: 'linear-gradient(135deg, #ff6b35 0%, #ffa500 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  fontWeight: '700',
                  display: 'inline'
                }}>{course}</span>
              </h2>
            </div>
            <div style={{ padding: '1.3rem 2rem' }}>
              <form onSubmit={async (e) => {
                e.preventDefault();

                // Validate mobile number
                if (enrollFormData.mobile.length !== 10) {
                  alert('Please enter a valid 10-digit mobile number');
                  return;
                }

                setIsSubmitting(true);
                const formData = { ...enrollFormData, course: course, category: 'Course Enrollment' };
                console.log('Enrollment Form Submitted', formData);

                const startTime = Date.now();

                // Development mode - simulate success
                if (process.env.NODE_ENV === 'development') {
                  await new Promise(resolve => setTimeout(resolve, 3000));
                  setPopupType('success');
                  setPopupMessage('Submitted Successfully');
                  setIsSyllabusForm(false);
                  setShowPopup(true);
                  setShowEnrollModal(false);
                  setEnrollFormData({ fullName: '', mobile: '', email: '', location: '' });
                  setIsSubmitting(false);
                  return;
                }

                try {
                  const response = await fetch('/api/register.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(formData)
                  });
                  console.log('Response status:', response.status);
                  const result = await response.json();
                  console.log('Result:', result);

                  // Ensure minimum 2 seconds have passed
                  const elapsed = Date.now() - startTime;
                  if (elapsed < 2000) {
                    await new Promise(resolve => setTimeout(resolve, 2000 - elapsed));
                  }

                  if (result.success) {
                    setPopupType('success');
                    setPopupMessage('Submitted Successfully');
                    setIsSyllabusForm(false);
                    setShowPopup(true);
                    setShowEnrollModal(false);
                    setEnrollFormData({ fullName: '', mobile: '', email: '', location: '' });
                  } else {
                    setPopupType('error');
                    setPopupMessage('Submission Failed');
                    setIsSyllabusForm(false);
                    setShowPopup(true);
                  }
                } catch (error) {
                  console.error('Error:', error);
                  const elapsed = Date.now() - startTime;
                  if (elapsed < 2000) {
                    await new Promise(resolve => setTimeout(resolve, 2000 - elapsed));
                  }
                  setPopupType('error');
                  setPopupMessage('Submission Failed');
                  setIsSyllabusForm(false);
                  setShowPopup(true);
                } finally {
                  setIsSubmitting(false);
                }
              }}>
                <div style={{ marginBottom: '0.7rem' }}>
                  <input
                    type="text"
                    placeholder="Name"
                    required
                    value={enrollFormData.fullName}
                    onChange={(e) => setEnrollFormData({ ...enrollFormData, fullName: e.target.value })}
                    onKeyPress={(e) => {
                      if (!/[a-zA-Z\s]/.test(e.key)) e.preventDefault();
                    }}
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.9rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '10px',
                      fontSize: '0.95rem',
                      outline: 'none',
                      boxShadow: 'inset 2px 2px 5px rgba(0,0,0,0.05), inset -2px -2px 5px rgba(255,255,255,0.8)',
                      transition: 'all 0.3s ease',
                      background: 'linear-gradient(145deg, #ffffff, #f9fafb)'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#003366';
                      e.target.style.boxShadow = 'inset 2px 2px 5px rgba(0,51,102,0.1), inset -2px -2px 5px rgba(255,255,255,0.9), 0 0 0 3px rgba(0,51,102,0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'inset 2px 2px 5px rgba(0,0,0,0.05), inset -2px -2px 5px rgba(255,255,255,0.8)';
                    }}
                  />
                </div>
                <div style={{ marginBottom: '0.7rem' }}>
                  <input
                    type="tel"
                    placeholder="Mobile Number"
                    required
                    value={enrollFormData.mobile}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setEnrollFormData({ ...enrollFormData, mobile: value });
                    }}
                    pattern="[0-9]{10}"
                    title="Please enter exactly 10 digits"
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.9rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '10px',
                      fontSize: '0.95rem',
                      outline: 'none',
                      boxShadow: 'inset 2px 2px 5px rgba(0,0,0,0.05), inset -2px -2px 5px rgba(255,255,255,0.8)',
                      transition: 'all 0.3s ease',
                      background: 'linear-gradient(145deg, #ffffff, #f9fafb)'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#003366';
                      e.target.style.boxShadow = 'inset 2px 2px 5px rgba(0,51,102,0.1), inset -2px -2px 5px rgba(255,255,255,0.9), 0 0 0 3px rgba(0,51,102,0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'inset 2px 2px 5px rgba(0,0,0,0.05), inset -2px -2px 5px rgba(255,255,255,0.8)';
                    }}
                  />
                </div>
                <div style={{ marginBottom: '0.7rem' }}>
                  <input
                    type="email"
                    placeholder="Email Address"
                    required
                    value={enrollFormData.email}
                    onChange={(e) => setEnrollFormData({ ...enrollFormData, email: e.target.value })}
                    pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.9rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '10px',
                      fontSize: '0.95rem',
                      outline: 'none',
                      boxShadow: 'inset 2px 2px 5px rgba(0,0,0,0.05), inset -2px -2px 5px rgba(255,255,255,0.8)',
                      transition: 'all 0.3s ease',
                      background: 'linear-gradient(145deg, #ffffff, #f9fafb)'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#003366';
                      e.target.style.boxShadow = 'inset 2px 2px 5px rgba(0,51,102,0.1), inset -2px -2px 5px rgba(255,255,255,0.9), 0 0 0 3px rgba(0,51,102,0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'inset 2px 2px 5px rgba(0,0,0,0.05), inset -2px -2px 5px rgba(255,255,255,0.8)';
                    }}
                  />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <select
                    value={enrollFormData.location}
                    onChange={(e) => setEnrollFormData({ ...enrollFormData, location: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.9rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '10px',
                      fontSize: '0.95rem',
                      outline: 'none',
                      cursor: 'pointer',
                      color: enrollFormData.location ? '#1f2937' : '#9ca3af',
                      boxShadow: 'inset 2px 2px 5px rgba(0,0,0,0.05), inset -2px -2px 5px rgba(255,255,255,0.8)',
                      transition: 'all 0.3s ease',
                      background: 'linear-gradient(145deg, #ffffff, #f9fafb)'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#003366';
                      e.target.style.boxShadow = 'inset 2px 2px 5px rgba(0,51,102,0.1), inset -2px -2px 5px rgba(255,255,255,0.9), 0 0 0 3px rgba(0,51,102,0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'inset 2px 2px 5px rgba(0,0,0,0.05), inset -2px -2px 5px rgba(255,255,255,0.8)';
                    }}
                  >
                    <option value="">Select Location</option>
                    <option value="Velachery">Velachery</option>
                    <option value="Chrompet">Chrompet</option>
                    <option value="Online">Online</option>
                  </select>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    width: '100%',
                    background: 'linear-gradient(135deg, #001f3f 0%, #003366 100%)',
                    color: 'white',
                    padding: '0.7rem',
                    border: 'none',
                    borderRadius: '25px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 15px rgba(0,31,63,0.3)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    opacity: isSubmitting ? 0.7 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!isSubmitting) {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 20px rgba(0,31,63,0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSubmitting) {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 15px rgba(0,31,63,0.3)';
                    }
                  }}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
              </form>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Syllabus Modal */}
      {showSyllabusModal && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '1rem',
            overflow: 'auto'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowSyllabusModal(false);
          }}
        >
          <div className="modal-content" style={{
            background: 'white',
            borderRadius: '16px',
            padding: '0',
            maxWidth: '420px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            position: 'relative',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            overflow: 'hidden'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #001f3f 0%, #003366 100%)',
              padding: '3rem 2rem',
              position: 'relative'
            }}>
              <button
                onClick={() => setShowSyllabusModal(false)}
                style={{
                  position: 'absolute',
                  top: '0.5rem',
                  right: '0.5rem',
                  background: 'rgba(255,255,255,0.3)',
                  border: 'none',
                  fontSize: '1.8rem',
                  cursor: 'pointer',
                  color: 'white',
                  lineHeight: 1,
                  fontWeight: '400',
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease',
                  zIndex: 10
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255,255,255,0.5)';
                  e.target.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255,255,255,0.3)';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                ×
              </button>
              <h2 style={{
                color: 'white',
                margin: 0,
                fontSize: '1.6rem',
                fontWeight: '600',
                textAlign: 'center',
                lineHeight: '1.5'
              }}>
                Unlock <span style={{
                  background: 'linear-gradient(135deg, #ff6b35 0%, #ffa500 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  fontWeight: '700',
                  display: 'inline'
                }}>Course Syllabus</span>
              </h2>
            </div>
            <div style={{ padding: '1.3rem 2rem' }}>
              <form onSubmit={async (e) => {
                e.preventDefault();

                // Validate mobile number
                if (syllabusFormData.mobile.length !== 10) {
                  alert('Please enter a valid 10-digit mobile number');
                  return;
                }

                setIsSubmitting(true);
                const formData = { ...syllabusFormData, course: course };
                console.log('Syllabus Form Submitted', formData);

                const startTime = Date.now();

                // Development mode - simulate success
                if (process.env.NODE_ENV === 'development') {
                  await new Promise(resolve => setTimeout(resolve, 2000));
                  setPopupType('success');
                  setPopupMessage('Syllabus has been sent to your mail');
                  setIsSyllabusForm(true);
                  setShowPopup(true);
                  setShowSyllabusModal(false);
                  setSyllabusFormData({ fullName: '', mobile: '', email: '' });
                  setIsSubmitting(false);
                  return;
                }

                try {
                  const response = await fetch('/api/syllabus-request.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(formData)
                  });
                  console.log('Response status:', response.status);
                  const result = await response.json();
                  console.log('Result:', result);

                  // Ensure minimum 2 seconds have passed
                  const elapsed = Date.now() - startTime;
                  if (elapsed < 2000) {
                    await new Promise(resolve => setTimeout(resolve, 2000 - elapsed));
                  }

                  if (result.success) {
                    setPopupType('success');
                    setPopupMessage('Syllabus has been sent to your mail');
                    setIsSyllabusForm(true);
                    setShowPopup(true);
                    setShowSyllabusModal(false);
                    setSyllabusFormData({ fullName: '', mobile: '', email: '' });
                  } else {
                    setPopupType('error');
                    setPopupMessage('Submission Failed');
                    setIsSyllabusForm(true);
                    setShowPopup(true);
                  }
                } catch (error) {
                  console.error('Error:', error);
                  const elapsed = Date.now() - startTime;
                  if (elapsed < 2000) {
                    await new Promise(resolve => setTimeout(resolve, 2000 - elapsed));
                  }
                  setPopupType('error');
                  setPopupMessage('Submission Failed');
                  setIsSyllabusForm(true);
                  setShowPopup(true);
                } finally {
                  setIsSubmitting(false);
                }
              }}>
                <div style={{ marginBottom: '0.7rem' }}>
                  <input
                    type="text"
                    placeholder="Name"
                    required
                    value={syllabusFormData.fullName}
                    onChange={(e) => setSyllabusFormData({ ...syllabusFormData, fullName: e.target.value })}
                    onKeyPress={(e) => {
                      if (!/[a-zA-Z\s]/.test(e.key)) e.preventDefault();
                    }}
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.9rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '10px',
                      fontSize: '0.95rem',
                      outline: 'none',
                      boxShadow: 'inset 2px 2px 5px rgba(0,0,0,0.05), inset -2px -2px 5px rgba(255,255,255,0.8)',
                      transition: 'all 0.3s ease',
                      background: 'linear-gradient(145deg, #ffffff, #f9fafb)'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#003366';
                      e.target.style.boxShadow = 'inset 2px 2px 5px rgba(0,51,102,0.1), inset -2px -2px 5px rgba(255,255,255,0.9), 0 0 0 3px rgba(0,51,102,0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'inset 2px 2px 5px rgba(0,0,0,0.05), inset -2px -2px 5px rgba(255,255,255,0.8)';
                    }}
                  />
                </div>
                <div style={{ marginBottom: '0.7rem' }}>
                  <input
                    type="tel"
                    placeholder="Mobile Number"
                    required
                    value={syllabusFormData.mobile}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setSyllabusFormData({ ...syllabusFormData, mobile: value });
                    }}
                    pattern="[0-9]{10}"
                    title="Please enter exactly 10 digits"
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.9rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '10px',
                      fontSize: '0.95rem',
                      outline: 'none',
                      boxShadow: 'inset 2px 2px 5px rgba(0,0,0,0.05), inset -2px -2px 5px rgba(255,255,255,0.8)',
                      transition: 'all 0.3s ease',
                      background: 'linear-gradient(145deg, #ffffff, #f9fafb)'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#003366';
                      e.target.style.boxShadow = 'inset 2px 2px 5px rgba(0,51,102,0.1), inset -2px -2px 5px rgba(255,255,255,0.9), 0 0 0 3px rgba(0,51,102,0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'inset 2px 2px 5px rgba(0,0,0,0.05), inset -2px -2px 5px rgba(255,255,255,0.8)';
                    }}
                  />
                </div>
                <div style={{ marginBottom: '0.7rem' }}>
                  <input
                    type="email"
                    placeholder="Email Address"
                    required
                    value={syllabusFormData.email}
                    onChange={(e) => setSyllabusFormData({ ...syllabusFormData, email: e.target.value })}
                    pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.9rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '10px',
                      fontSize: '0.95rem',
                      outline: 'none',
                      boxShadow: 'inset 2px 2px 5px rgba(0,0,0,0.05), inset -2px -2px 5px rgba(255,255,255,0.8)',
                      transition: 'all 0.3s ease',
                      background: 'linear-gradient(145deg, #ffffff, #f9fafb)'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#003366';
                      e.target.style.boxShadow = 'inset 2px 2px 5px rgba(0,51,102,0.1), inset -2px -2px 5px rgba(255,255,255,0.9), 0 0 0 3px rgba(0,51,102,0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'inset 2px 2px 5px rgba(0,0,0,0.05), inset -2px -2px 5px rgba(255,255,255,0.8)';
                    }}
                  />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <input
                    type="text"
                    value={course}
                    readOnly
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.9rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '10px',
                      fontSize: '0.95rem',
                      outline: 'none',
                      boxShadow: 'inset 2px 2px 5px rgba(0,0,0,0.05), inset -2px -2px 5px rgba(255,255,255,0.8)',
                      background: '#f3f4f6',
                      color: '#6b7280',
                      cursor: 'not-allowed'
                    }}
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    width: '100%',
                    background: 'linear-gradient(135deg, #001f3f 0%, #003366 100%)',
                    color: 'white',
                    padding: '0.7rem',
                    border: 'none',
                    borderRadius: '25px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 15px rgba(0,31,63,0.3)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    opacity: isSubmitting ? 0.7 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!isSubmitting) {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 20px rgba(0,31,63,0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSubmitting) {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 15px rgba(0,31,63,0.3)';
                    }
                  }}
                >
                  {isSubmitting ? 'Submitting...' : 'Get Syllabus'}
                </button>
              </form>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Responsive Styles */}
      <style>{`
        /* Hero Section Responsive */
        @media (max-width: 968px) {
          section:first-of-type > div > div {
            grid-template-columns: 1fr !important;
          }
        }
        
        /* MOBILE SPECIFIC STYLES */
        @media (max-width: 768px) {
          /* Hero Section - Mobile */
          .hero-fade-in {
            padding: 0rem 1.5rem 8rem !important;
            padding-top: 0px !important;
            margin-top: 0 !important;
            min-height: 600px !important;
          }

          .hero-fade-in > div {
            padding-top: 8rem !important;
          }
          
          .hero-fade-in h1 {
            font-size: 2rem !important;
            margin-bottom: 1rem !important;
          }
          
          .hero-fade-in p {
            font-size: 1rem !important;
            margin-bottom: 1.5rem !important;
          }
          
          /* Keep buttons in one line - mobile */
          .hero-fade-in > div > div {
            flex-direction: row !important;
            gap: 0.8rem !important;
            justify-content: center !important;
          }
          
          .hero-fade-in > div > div button {
            padding: 0.75rem 1.2rem !important;
            font-size: 0.85rem !important;
            white-space: nowrap !important;
          }
          
          /* Course Highlights - Mobile */
          section > div {
            grid-template-columns: 1fr !important;
          }
          
          /* Course Highlights Title - One Line */
          section > div > h2 {
            white-space: nowrap !important;
            overflow: visible !important;
          }
          
          /* Hide computer image on mobile */
          section > div > div:nth-child(2) > div:first-child {
            display: none !important;
          }
          
          section > div > div:nth-child(2) > div:last-child {
            order: 1;
          }
          
          /* Course Highlight Cards - Stack vertically, centered, same size */
          section > div > div:nth-child(2) > div:last-child {
            flex-direction: column !important;
            align-items: center !important;
            margin-right: 0 !important;
          }
          
          section > div > div:nth-child(2) > div:last-child > div {
            margin-top: 0 !important;
            width: 100% !important;
            max-width: 280px !important;
            display: flex !important;
            flex-direction: column !important;
            gap: 1.5rem !important;
          }
          
          section > div > div:nth-child(2) > div:last-child > div > div {
            width: 280px !important;
            min-height: 140px !important;
          }
          
          /* Training Benefits - Mobile */
          .training-benefits-grid {
            grid-template-columns: 1fr !important;
            gap: 0 !important;
          }
          
          /* Hide training benefits image on mobile */
          .training-benefits-grid > div:last-child {
            display: none !important;
          }
          
          /* Center training benefits content */
          .training-benefits-grid > div:first-child {
            text-align: center !important;
            max-width: 100% !important;
          }
          
          .training-benefits-grid > div:first-child h2 {
            font-size: 1.8rem !important;
            text-align: center !important;
          }
          
          .training-benefits-grid > div:first-child p {
            font-size: 0.95rem !important;
          }
          
          .training-benefits-grid > div:first-child > div {
            justify-content: center !important;
            flex-wrap: wrap !important;
            gap: 1.5rem !important;
          }
          
          .training-benefits-grid > div:first-child > div > div {
            flex: 0 1 auto !important;
            min-width: 0 !important;
          }
          
          .training-benefits-grid > div:first-child > div > div > div:first-child {
            font-size: 1.5rem !important;
          }
          
          .training-benefits-grid > div:first-child > div > div > div:last-child {
            font-size: 0.75rem !important;
            max-width: 100px !important;
          }
          
          .training-benefits-grid > div:first-child > button {
            display: block !important;
            margin: 0.5rem auto 0 auto !important;
          }
          
          /* Course Modules - Mobile */
          .course-modules-grid {
            grid-template-columns: 1fr !important;
            gap: 0.9rem !important;
            padding: 0 !important;
          }
          
          .module-item {
            padding: 0.8rem 1.5rem !important;
            min-height: 70px !important;
            height: 70px !important;
            display: flex !important;
            flex-direction: row !important;
            align-items: center !important;
            justify-content: flex-start !important;
            gap: 0 !important;
            border-left: 6px solid #ff6b35 !important;
            border-radius: 14px !important;
            box-sizing: border-box !important;
          }
          
          .module-item > .module-icon,
          .module-item .module-icon,
          .module-icon {
            display: none !important;
            visibility: hidden !important;
            width: 0 !important;
            height: 0 !important;
            opacity: 0 !important;
            overflow: hidden !important;
          }
          
          .module-item > span,
          .module-text {
            font-size: 0.9rem !important;
            line-height: 1.4 !important;
            flex: 1 !important;
            padding: 0 !important;
            margin: 0 !important;
            text-align: left !important;
            display: block !important;
            width: 100% !important;
          }
          
          /* Certificate Section - Mobile */
          .certificate-section-grid {
            grid-template-columns: 1fr !important;
            gap: 2rem !important;
          }
          
          /* Title comes first on mobile */
          .certificate-section-grid > div:first-child {
            order: 1 !important;
            text-align: left !important;
          }
          
          .certificate-section-grid > div:first-child h2 {
            font-size: 1.25rem !important;
            text-align: center !important;
            line-height: 1.5 !important;
            white-space: normal !important;
          }
          
          /* Certificate section on mobile */
          .certificate-section-grid > div:last-child {
            order: 2 !important;
            min-height: auto !important;
          }
          
          /* Hide decorative elements on mobile */
          .certificate-section-grid > div:last-child > div:nth-child(1),
          .certificate-section-grid > div:last-child > div:nth-child(2),
          .certificate-section-grid > div:last-child > div:nth-child(3) {
            display: none !important;
          }
          
          /* Certificate frame - remove blue background */
          .certificate-section-grid > div:last-child > div:nth-child(4) {
            background: transparent !important;
            padding: 0 !important;
            box-shadow: none !important;
            width: 100% !important;
            max-width: 100% !important;
            height: auto !important;
          }
          
          /* White inner frame - fit to mobile */
          .certificate-section-grid > div:last-child > div:nth-child(4) > div {
            display: flex !important;
            width: 100% !important;
            padding: 0 !important;
            box-shadow: none !important;
          }
          
          /* Certificate image - fit to mobile */
          .certificate-section-grid > div:last-child img {
            width: 100% !important;
            height: auto !important;
          }
          
          /* Related Courses - One card at a time with swipe */
          .related-courses-carousel {
            overflow-x: auto !important;
            overflow-y: visible !important;
            scroll-snap-type: x mandatory !important;
            -webkit-overflow-scrolling: touch !important;
            scrollbar-width: none !important;
            -ms-overflow-style: none !important;
            padding: 10px 0 !important;
          }
          
          .related-courses-carousel::-webkit-scrollbar {
            display: none !important;
          }
          
          .related-courses-carousel > div {
            display: flex !important;
            flex-wrap: nowrap !important;
            gap: 1rem !important;
          }
          
          .related-courses-carousel > div > div {
            min-width: calc(100% - 20px) !important;
            max-width: calc(100% - 20px) !important;
            flex-shrink: 0 !important;
            scroll-snap-align: center !important;
            scroll-snap-stop: always !important;
            margin: 0 10px !important;
          }
          
          .carousel-nav-button {
            display: none !important;
          }
          
          /* Section padding adjustments for mobile */
          section {
            padding: 3rem 1.5rem !important;
          }
          
          section h2 {
            font-size: 2rem !important;
            margin-bottom: 2rem !important;
          }
          
          /* Related Courses Section - Mobile */
          section:last-of-type h2 {
            margin-bottom: 0.5rem !important;
          }
          
          section:last-of-type > div > div:first-child {
            margin-bottom: 1.5rem !important;
          }
        }
        
        /* Modal Responsive */
        @media (max-width: 640px) {
          .modal-content {
            width: 95% !important;
            max-width: none !important;
            background: white !important;
            box-shadow: 0 20px 60px rgba(0,0,0,0.3) !important;
            border-radius: 16px !important;
          }
          
          .modal-content > div:first-child {
            padding: 1.5rem 2rem !important;
            border-radius: 16px 16px 0 0 !important;
          }
          
          .modal-content > div:first-child h2 {
            font-size: 1.2rem !important;
            line-height: 1.3 !important;
          }
          
          .modal-content > div:last-child {
            background: white !important;
            padding: 1rem !important;
            border-radius: 0 0 16px 16px !important;
          }
        }
      `}</style>

      <FormSuccessPopup
        isOpen={showPopup}
        onClose={() => setShowPopup(false)}
        type={popupType}
        message={popupMessage}
        isSyllabusForm={isSyllabusForm}
      />
      <FormLoader isLoading={isSubmitting} />
      <Footer
        onHomeClick={onHomeClick}
        onAboutClick={onAboutClick}
        onContactClick={onContactClick}
        onCourseSelect={onCourseSelect}
      />
    </div>
  );
};

export default CourseDetails;
